import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from '../contexts/UserContext';
import {
  db,
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  firebaseConfig,
  arrayUnion,
  writeBatch
} from '../firebase';
import { SMS_CARRIERS, carrierLabel } from '../constants/smsCarriers';

// Build the emailList and smsNumbers arrays that the alerting backend
// (server.py, Cloud Functions) reads, from a list of full user records.
// Users without an email are skipped silently. Users without both phone
// and smsCarrier do not appear in smsNumbers.
function deriveAlertChannels(users) {
  const emailList = users
    .filter(u => u && u.email)
    .map(u => u.email);
  const smsNumbers = users
    .filter(u => u && u.phone && u.smsCarrier)
    .map(u => `${u.phone}@${u.smsCarrier}`);
  return { emailList, smsNumbers };
}

// Helper to determine device online/offline status
function getDeviceStatus(device, thresholdMinutes = 30) {
  if (!device.last_seen) return { status: 'unknown', lastSeenText: 'Never' };

  const lastSeenDate = device.last_seen.toDate ? device.last_seen.toDate() : new Date(device.last_seen);
  const now = new Date();
  const diffMs = now - lastSeenDate;
  const diffMinutes = Math.floor(diffMs / 60000);

  let lastSeenText;
  if (diffMinutes < 1) {
    lastSeenText = 'Just now';
  } else if (diffMinutes < 60) {
    lastSeenText = `${diffMinutes}m ago`;
  } else if (diffMinutes < 1440) {
    lastSeenText = `${Math.floor(diffMinutes / 60)}h ago`;
  } else {
    lastSeenText = `${Math.floor(diffMinutes / 1440)}d ago`;
  }

  // Only use last_seen timestamp for status (ignore stale isOffline flag from Firestore)
  const isOffline = diffMinutes > thresholdMinutes;
  return {
    status: isOffline ? 'offline' : 'online',
    lastSeenText,
    diffMinutes
  };
}

// Categorize WiFi signal % per the operations runbook
function getWifiCategory(percent) {
  if (percent >= 80) return { label: 'Excellent', className: 'excellent' };
  if (percent >= 60) return { label: 'Good', className: 'good' };
  if (percent >= 40) return { label: 'Fair', className: 'fair' };
  return { label: 'Poor', className: 'poor' };
}

// WiFi signal indicator: 4 bars + percent. Hidden if no signal data.
function WifiSignal({ signal, offline }) {
  if (offline) {
    return (
      <span className="wifi-signal offline" title="Device offline — last WiFi reading is stale">
        <span className="wifi-label">WiFi</span>
        <span className="wifi-percent">Not connected</span>
      </span>
    );
  }
  if (!signal || typeof signal.percent !== 'number') return null;
  const percent = signal.percent;
  const cat = getWifiCategory(percent);
  const filledBars = percent >= 80 ? 4 : percent >= 60 ? 3 : percent >= 40 ? 2 : percent > 0 ? 1 : 0;
  const dbmText = typeof signal.dbm === 'number' ? `${signal.dbm} dBm` : '';
  const ifaceText = signal.iface ? ` on ${signal.iface}` : '';
  const title = `WiFi: ${cat.label} (${percent}%${dbmText ? ', ' + dbmText : ''}${ifaceText})`;
  return (
    <span className={`wifi-signal ${cat.className}`} title={title}>
      <span className="wifi-label">WiFi</span>
      <span className="wifi-bars" aria-hidden="true">
        {[1, 2, 3, 4].map((i) => (
          <span key={i} className={`wifi-bar ${i <= filledBars ? 'on' : 'off'}`} />
        ))}
      </span>
      <span className="wifi-percent">{percent}%</span>
    </span>
  );
}

// Helper to check if sensor data is stale
const isSensorStale = (lastReadingAt, thresholdMinutes = 20) => {
  if (!lastReadingAt) return true;
  const lastTime = lastReadingAt.toDate ? lastReadingAt.toDate() : new Date(lastReadingAt);
  const thresholdMs = thresholdMinutes * 60 * 1000;
  return (Date.now() - lastTime.getTime()) > thresholdMs;
};

const getSensorTempDisplay = (sensor, device = null, thresholdMinutes = 20) => {
  // Device down wins over a sensor fault: when the whole device is offline a
  // leftover malfunction flag would otherwise mislabel it as "Sensor Error".
  if (device && getDeviceStatus(device, thresholdMinutes).status === 'offline') return 'Device Offline';
  // Sensor-specific malfunction (read error / probe reads ~185°F).
  if (sensor?.malfunction_alert_active) return 'Sensor Error';
  // Sensor stopped reporting even though the device is still online.
  if (isSensorStale(sensor?.last_reading_at, thresholdMinutes)) return 'Offline';
  if (!sensor?.current_temp) return '--°F';
  return `${sensor.current_temp.toFixed(1)}°F`;
};

// Picker for selecting portfolio users as alert contacts. Used by per-sensor
// alerts (channelMode='email-and-sms') and portfolio connection alerts
// (channelMode='email-only'). Shows a search filter and a checkbox row per
// user with a hint about whether SMS will work for them.
function UserAlertPicker({
  users,
  selectedIds,
  onToggle,
  search,
  onSearchChange,
  channelMode,
  emptyHint
}) {
  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.name || '').toLowerCase().includes(q) ||
           (u.email || '').toLowerCase().includes(q);
  });
  const showSmsColumn = channelMode === 'email-and-sms';
  const selectedUsers = selectedIds
    .map(id => users.find(u => u.uid === id))
    .filter(Boolean);
  const stalePicks = selectedIds.filter(id => !users.find(u => u.uid === id));
  const hasUsers = users.length > 0;

  return (
    <div className="user-alert-picker">
      {!hasUsers && <p className="hint">{emptyHint}</p>}

      {hasUsers && (
        <>
          <div className="picker-toolbar">
            <input
              type="text"
              className="picker-search"
              placeholder="Search users..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            <span className="picker-count">
              {selectedUsers.length} of {users.length} selected
            </span>
          </div>

          <div className="picker-rows">
            {filtered.map(u => {
              const isSelected = selectedIds.includes(u.uid);
              const smsReady = !!(u.phone && u.smsCarrier);
              return (
                <label
                  key={u.uid}
                  className={`picker-row ${isSelected ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggle(u.uid)}
                  />
                  <div className="picker-user">
                    <div className="picker-name">{u.name}</div>
                    <div className="picker-email">{u.email}</div>
                  </div>
                  {showSmsColumn && (
                    <div className={`picker-sms ${smsReady ? 'ready' : 'missing'}`}>
                      {smsReady
                        ? <>SMS via {carrierLabel(u.smsCarrier)}</>
                        : <>No SMS — phone/carrier not set</>}
                    </div>
                  )}
                </label>
              );
            })}
            {filtered.length === 0 && (
              <p className="hint">No users match your search.</p>
            )}
          </div>
        </>
      )}

      {stalePicks.length > 0 && (
        <p className="picker-stale-warning">
          {stalePicks.length} previously-selected user{stalePicks.length === 1 ? '' : 's'}
          {' '}no longer belong to this portfolio and will be dropped on save.
        </p>
      )}
    </div>
  );
}

function Portfolios({ homeTick = 0 }) {
  const {
    user,
    userProfile,
    isSuperAdmin,
    isSystemAdmin,
    canAccessPortfolio,
    canEditInPortfolio,
    canManageUsersInPortfolio,
    getPortfolioRole,
    getRoleDisplayName,
    getInvitableRoles
  } = useUser();

  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);

  // Refresh tick to force status recalculation every 15 seconds
  const [refreshTick, setRefreshTick] = useState(0);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sensors, setSensors] = useState([]);
  const [devices, setDevices] = useState([]);
  // Memoize device IDs to prevent re-fetching events on every device data update
  const deviceIds = useMemo(() => devices.map(d => d.id).sort().join(','), [devices]);
  const [showNewBuilding, setShowNewBuilding] = useState(false);
  const [newBuildingName, setNewBuildingName] = useState('');
  const [newBuildingAddress, setNewBuildingAddress] = useState('');
  const [showNewSensor, setShowNewSensor] = useState(false);
  const [newSensorName, setNewSensorName] = useState('');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [selectedSensorId, setSelectedSensorId] = useState('');

  // Sensor page view (instead of modal)
  const [viewingSensor, setViewingSensor] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');

  // Portfolio content view: 'cards' (buildings + device blocks) or 'table'
  // (compact all-sensors table for scanning temps quickly)
  const [portfolioView, setPortfolioView] = useState(() => {
    try { return localStorage.getItem('tempsensor_portfolio_view') || 'cards'; }
    catch (e) { return 'cards'; }
  });
  const switchPortfolioView = (mode) => {
    setPortfolioView(mode);
    try { localStorage.setItem('tempsensor_portfolio_view', mode); } catch (e) {}
  };
  const [portfolioTableSort, setPortfolioTableSort] = useState({ key: 'building', dir: 1 });

  // All-portfolios overview data (shown when no portfolio is selected):
  // every device/sensor/building across every portfolio the user can see
  const [overview, setOverview] = useState({ sensors: [], devices: [], buildings: [] });
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [editThreshold, setEditThreshold] = useState(45);
  const [editSensorName, setEditSensorName] = useState('');
  const [editErrorDelay, setEditErrorDelay] = useState(1);  // minutes before error alert

  // Sensor alert contacts — user-id based picker
  // The sensor doc holds `contactUserIds: [uid, ...]` (source of truth) and
  // denormalized `emailList` / `smsNumbers` derived from those users for the
  // alerting backend.
  const [editContactUserIds, setEditContactUserIds] = useState([]);
  const [legacySensorContacts, setLegacySensorContacts] = useState([]);
  const [contactPickerSearch, setContactPickerSearch] = useState('');

  // Cache of portfolio users — always loaded when a portfolio is selected,
  // shared by the team panel, the sensor alert-contact picker, and the
  // connection-alert picker. Map keyed by uid.
  const [portfolioUsers, setPortfolioUsers] = useState([]);

  // Alarm log
  const [alarmLogs, setAlarmLogs] = useState([]);
  const [loadingAlarms, setLoadingAlarms] = useState(false);

  // Device settings modal
  const [editingDevice, setEditingDevice] = useState(null);
  const [editDeviceName, setEditDeviceName] = useState('');
  const [editDeviceBuilding, setEditDeviceBuilding] = useState('');

  // Temperature history
  const [readings, setReadings] = useState([]);
  const [loadingReadings, setLoadingReadings] = useState(false);
  const [trendTimeRange, setTrendTimeRange] = useState('24h');
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Team management
  const [showTeamPanel, setShowTeamPanel] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('customer_viewer');
  const [inviteLoading, setInviteLoading] = useState(false);

  // New portfolio modal (super admin only)
  const [showNewPortfolio, setShowNewPortfolio] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');

  // Connection alerts page (full page view like sensor page)
  const [viewingConnectionAlerts, setViewingConnectionAlerts] = useState(false);
  const [connectionAlertsTab, setConnectionAlertsTab] = useState('status');
  const [connectionAlertsEnabled, setConnectionAlertsEnabled] = useState(true);
  const [offlineThresholdMinutes, setOfflineThresholdMinutes] = useState(20);
  const [savingConnectionSettings, setSavingConnectionSettings] = useState(false);
  const [connectionEvents, setConnectionEvents] = useState([]);
  const [loadingConnectionEvents, setLoadingConnectionEvents] = useState(false);

  // Bulk alert-contact editor (full page, lets admins assign a set of users
  // to a set of sensors at once). bulkAssignments is the working copy keyed by
  // `${deviceId}/${sensorId}`; bulkOriginal is the snapshot at open time so we
  // only persist sensors whose membership actually changed.
  const [viewingBulkAlerts, setViewingBulkAlerts] = useState(false);
  const [bulkAssignments, setBulkAssignments] = useState({});
  const [bulkOriginal, setBulkOriginal] = useState({});
  const [bulkSelectedUserIds, setBulkSelectedUserIds] = useState([]);
  const [bulkSelectedSensorKeys, setBulkSelectedSensorKeys] = useState([]);
  const [savingBulk, setSavingBulk] = useState(false);

  // Bulk alarm-settings editor (full page, lets admins set the temperature
  // threshold and sensor-error delay on many sensors at once). Mirrors the
  // shape of the bulk alert-contacts state above.
  const [viewingBulkSettings, setViewingBulkSettings] = useState(false);
  const [bulkSettings, setBulkSettings] = useState({});
  const [bulkSettingsOriginal, setBulkSettingsOriginal] = useState({});
  const [bulkSettingsSelectedKeys, setBulkSettingsSelectedKeys] = useState([]);
  const [bulkApplyThreshold, setBulkApplyThreshold] = useState(true);
  const [bulkApplyErrorDelay, setBulkApplyErrorDelay] = useState(true);
  const [bulkThresholdValue, setBulkThresholdValue] = useState(45);
  const [bulkErrorDelayValue, setBulkErrorDelayValue] = useState(1);
  const [savingBulkSettings, setSavingBulkSettings] = useState(false);

  // Connection alert contacts (portfolio-level) — user-id based picker.
  // Stored on the portfolio doc as `connectionAlertUserIds: [uid, ...]` with
  // a denormalized `connectionAlertEmails` array for the Cloud Function.
  const [connectionAlertUserIds, setConnectionAlertUserIds] = useState([]);
  const [legacyConnectionContacts, setLegacyConnectionContacts] = useState([]);
  const [connContactPickerSearch, setConnContactPickerSearch] = useState('');

  // Permission helpers for current portfolio
  const canEdit = selectedPortfolio ? canEditInPortfolio(selectedPortfolio.members || {}) : false;
  const canManageUsers = selectedPortfolio ? canManageUsersInPortfolio(selectedPortfolio.members || {}) : false;
  const myRole = selectedPortfolio ? getPortfolioRole(selectedPortfolio.members || {}) : null;

  // Refresh status displays every 15 seconds for real-time feel
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTick(tick => tick + 1);
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  // The Portfolios header button bumps homeTick — jump back to the
  // all-portfolios overview.
  useEffect(() => {
    if (homeTick === 0) return;
    setSelectedPortfolio(null);
    setSelectedBuilding(null);
    setViewingSensor(null);
    setShowTeamPanel(false);
  }, [homeTick]);

  // Load the all-portfolios overview whenever we're on the "no portfolio
  // selected" home view. One-shot fetch + refresh every 60s (cheaper than
  // live listeners across every portfolio at once).
  useEffect(() => {
    if (selectedPortfolio || portfolios.length === 0) return;
    let cancelled = false;

    const load = async () => {
      try {
        const allDevices = [];
        const allSensors = [];
        const allBuildings = [];
        for (const p of portfolios) {
          const [devSnap, bldSnap] = await Promise.all([
            getDocs(collection(db, 'customer_portfolios', p.id, 'devices')),
            getDocs(collection(db, 'customer_portfolios', p.id, 'buildings'))
          ]);
          bldSnap.forEach((b) => allBuildings.push({ ...b.data(), id: b.id, portfolioId: p.id }));
          const devs = [];
          devSnap.forEach((d) => devs.push({ ...d.data(), id: d.id, portfolioId: p.id }));
          allDevices.push(...devs);
          const sensorSnaps = await Promise.all(devs.map((d) =>
            getDocs(collection(db, 'customer_portfolios', p.id, 'devices', d.id, 'sensors'))
          ));
          sensorSnaps.forEach((snap, i) => {
            snap.forEach((s) => allSensors.push({
              ...s.data(),
              id: s.id,
              sensor_id: s.id,
              device_id: devs[i].id,
              portfolioId: p.id,
              portfolioName: p.name
            }));
          });
        }
        if (!cancelled) {
          setOverview({ sensors: allSensors, devices: allDevices, buildings: allBuildings });
          setOverviewLoading(false);
        }
      } catch (error) {
        console.error('Error loading overview:', error);
        if (!cancelled) setOverviewLoading(false);
      }
    };

    setOverviewLoading(true);
    load();
    const interval = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [selectedPortfolio, portfolios]);

  // Fetch portfolios - system admins get all, others get by ID
  useEffect(() => {
    if (!user) return;

    // System admins can query all portfolios
    if (isSystemAdmin()) {
      const unsubscribe = onSnapshot(
        collection(db, 'customer_portfolios'),
        (snapshot) => {
          const list = [];
          snapshot.forEach((doc) => {
            list.push({ id: doc.id, ...doc.data() });
          });
          list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setPortfolios(list);
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching portfolios:', error);
          setLoading(false);
        }
      );
      return () => unsubscribe();
    }

    // Non-admins: fetch portfolios by ID from their portfolioIds array
    const portfolioIds = userProfile?.portfolioIds || [];
    if (portfolioIds.length === 0) {
      setPortfolios([]);
      setLoading(false);
      return;
    }

    // Set up listeners for each portfolio
    const unsubscribes = [];
    const portfolioMap = new Map();

    portfolioIds.forEach((portfolioId) => {
      const unsubscribe = onSnapshot(
        doc(db, 'customer_portfolios', portfolioId),
        (docSnap) => {
          if (docSnap.exists()) {
            portfolioMap.set(portfolioId, { id: docSnap.id, ...docSnap.data() });
          } else {
            portfolioMap.delete(portfolioId);
          }
          // Update state with all portfolios
          const list = Array.from(portfolioMap.values());
          list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setPortfolios(list);
          setLoading(false);
        },
        (error) => {
          console.error(`Error fetching portfolio ${portfolioId}:`, error);
        }
      );
      unsubscribes.push(unsubscribe);
    });

    return () => unsubscribes.forEach(unsub => unsub());
  }, [user, userProfile?.portfolioIds, isSystemAdmin]);

  // Fetch buildings when portfolio selected
  useEffect(() => {
    if (!selectedPortfolio) {
      setBuildings([]);
      setSelectedBuilding(null);
      return;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'customer_portfolios', selectedPortfolio.id, 'buildings'),
      (snapshot) => {
        const list = [];
        snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setBuildings(list);
      }
    );
    return () => unsubscribe();
  }, [selectedPortfolio]);

  // Load connection alert settings when portfolio selected
  useEffect(() => {
    if (!selectedPortfolio) return;
    setConnectionAlertsEnabled(selectedPortfolio.connectionAlertsEnabled !== false);
    setOfflineThresholdMinutes(Math.max(2, selectedPortfolio.offlineThresholdMinutes || 20));
    setConnectionAlertUserIds(selectedPortfolio.connectionAlertUserIds || []);
    setLegacyConnectionContacts(selectedPortfolio.connectionAlertContacts || []);
  }, [selectedPortfolio]);

  // Load portfolio users (with phone/carrier) whenever the portfolio changes.
  // Re-runs when team membership changes so the picker stays current.
  useEffect(() => {
    if (!selectedPortfolio) {
      setPortfolioUsers([]);
      return;
    }
    let cancelled = false;
    const loadUsers = async () => {
      const members = selectedPortfolio.members || {};
      const list = [];
      for (const [uid, memberData] of Object.entries(members)) {
        try {
          const userDoc = await getDoc(doc(db, 'users', uid));
          if (userDoc.exists()) {
            const u = userDoc.data();
            list.push({
              uid,
              email: u.email || '',
              name: u.name || u.email || uid,
              phone: u.phone || '',
              smsCarrier: u.smsCarrier || '',
              role: memberData.role
            });
          }
        } catch (err) {
          console.error('Failed to load user', uid, err);
        }
      }
      if (cancelled) return;
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setPortfolioUsers(list);
    };
    loadUsers();
    return () => { cancelled = true; };
  }, [selectedPortfolio]);

  // Fetch connection events when viewing connection alerts
  useEffect(() => {
    if (!viewingConnectionAlerts || connectionAlertsTab !== 'logs' || !selectedPortfolio) return;
    setLoadingConnectionEvents(true);

    // Fetch connection events from all devices
    const fetchEvents = async () => {
      try {
        const allEvents = [];
        for (const device of devices) {
          const eventsRef = collection(db, 'customer_portfolios', selectedPortfolio.id, 'devices', device.id, 'connection_events');
          const eventsQuery = query(eventsRef, orderBy('timestamp', 'desc'), limit(50));
          const snapshot = await getDocs(eventsQuery);
          snapshot.forEach(doc => {
            allEvents.push({
              id: doc.id,
              deviceId: device.id,
              deviceName: device.name || device.id,
              ...doc.data()
            });
          });
        }
        // Sort all events by timestamp descending
        allEvents.sort((a, b) => {
          const aTime = a.timestamp?.toDate?.() || new Date(0);
          const bTime = b.timestamp?.toDate?.() || new Date(0);
          return bTime - aTime;
        });
        setConnectionEvents(allEvents.slice(0, 100));
      } catch (error) {
        console.error('Error fetching connection events:', error);
      } finally {
        setLoadingConnectionEvents(false);
      }
    };
    fetchEvents();
  }, [viewingConnectionAlerts, connectionAlertsTab, selectedPortfolio, deviceIds]);

  // Fetch devices for portfolio
  useEffect(() => {
    if (!selectedPortfolio) {
      setDevices([]);
      return;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'customer_portfolios', selectedPortfolio.id, 'devices'),
      (snapshot) => {
        const list = [];
        snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
        setDevices(list);
      }
    );
    return () => unsubscribe();
  }, [selectedPortfolio]);

  // Fetch sensors from ALL devices in the portfolio
  useEffect(() => {
    if (!selectedPortfolio || devices.length === 0) {
      setSensors([]);
      return;
    }

    // Subscribe to sensors for each device
    const unsubscribes = [];
    const allSensors = {};

    devices.forEach(device => {
      const sensorsRef = collection(db, 'customer_portfolios', selectedPortfolio.id, 'devices', device.id, 'sensors');
      const unsub = onSnapshot(sensorsRef, (snapshot) => {
        // Clear this device's sensors first
        Object.keys(allSensors).forEach(key => {
          if (key.startsWith(`${device.id}_`)) delete allSensors[key];
        });
        snapshot.forEach((doc) => {
          allSensors[`${device.id}_${doc.id}`] = {
            id: doc.id,
            device_id: device.id,
            device_name: device.name,
            building_id: device.building_id,
            ...doc.data()
          };
        });
        // Update sensors state with all collected sensors
        const sensorList = Object.values(allSensors);
        sensorList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setSensors(sensorList);
      });
      unsubscribes.push(unsub);
    });

    return () => unsubscribes.forEach(unsub => unsub());
  }, [selectedPortfolio, devices]);

  // Keep viewingSensor in sync with real-time sensor data
  useEffect(() => {
    if (!viewingSensor) return;

    const updatedSensor = sensors.find(s => s.id === viewingSensor.id);
    if (updatedSensor && updatedSensor.current_temp !== viewingSensor.current_temp) {
      setViewingSensor(prev => ({
        ...prev,
        current_temp: updatedSensor.current_temp,
        last_reading_at: updatedSensor.last_reading_at
      }));
    }
  }, [sensors, viewingSensor]);

  // Real-time temperature readings when trends tab is shown
  useEffect(() => {
    if (activeTab !== 'trends' || !viewingSensor || !selectedPortfolio || !viewingSensor.device_id) {
      setReadings([]);
      return;
    }

    setLoadingReadings(true);
    // NEW STRUCTURE: readings are under devices/sensors
    const readingsRef = collection(
      db, 'customer_portfolios', selectedPortfolio.id,
      'devices', viewingSensor.device_id,
      'sensors', viewingSensor.id, 'readings'
    );
    // Query based on selected time range so 7d actually fetches 7 days of data
    const timeRangeHours = { '12h': 12, '24h': 24, '7d': 24 * 7 };
    const cutoffDate = new Date(Date.now() - (timeRangeHours[trendTimeRange] * 60 * 60 * 1000));
    const q = query(readingsRef, where('timestamp', '>=', cutoffDate), orderBy('timestamp', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        list.push({
          id: doc.id,
          temp: data.temp,
          timestamp: data.timestamp?.toDate()
        });
      });
      setReadings(list);
      setLoadingReadings(false);
    }, (error) => {
      console.error('Error fetching readings:', error);
      setLoadingReadings(false);
    });

    return () => unsubscribe();
  }, [activeTab, viewingSensor, selectedPortfolio, selectedBuilding, trendTimeRange]);

  // Fetch alarm logs when alarms tab is shown
  useEffect(() => {
    if (activeTab !== 'alarms' || !viewingSensor || !selectedPortfolio || !viewingSensor.device_id) {
      return;
    }

    const fetchAlarms = async () => {
      setLoadingAlarms(true);
      try {
        // NEW STRUCTURE: alarms are under devices/sensors
        const alarmsRef = collection(
          db, 'customer_portfolios', selectedPortfolio.id,
          'devices', viewingSensor.device_id,
          'sensors', viewingSensor.id, 'alarms'
        );
        const q = query(alarmsRef, orderBy('timestamp', 'desc'), limit(50));
        const snapshot = await getDocs(q);
        const list = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          list.push({
            id: doc.id,
            temp: data.temp,
            threshold: data.threshold,
            message: data.message,
            notified: data.notified || [],
            timestamp: data.timestamp?.toDate()
          });
        });
        setAlarmLogs(list);
      } catch (error) {
        console.error('Error fetching alarms:', error);
      }
      setLoadingAlarms(false);
    };

    fetchAlarms();
  }, [activeTab, viewingSensor, selectedPortfolio, selectedBuilding]);

  // Fetch team members when team panel is shown
  useEffect(() => {
    if (!showTeamPanel || !selectedPortfolio) return;

    const fetchTeamMembers = async () => {
      const members = selectedPortfolio.members || {};
      const memberList = [];

      for (const [uid, memberData] of Object.entries(members)) {
        try {
          const userDoc = await getDoc(doc(db, 'users', uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            memberList.push({
              uid,
              email: userData.email,
              name: userData.name,
              role: memberData.role,
              joinedAt: memberData.joinedAt
            });
          } else {
            memberList.push({
              uid,
              email: 'Unknown',
              name: 'Unknown User',
              role: memberData.role,
              joinedAt: memberData.joinedAt
            });
          }
        } catch (error) {
          console.error('Error fetching team member:', error);
        }
      }

      setTeamMembers(memberList);
    };

    fetchTeamMembers();
  }, [showTeamPanel, selectedPortfolio]);

  const handleCreatePortfolio = async () => {
    if (!newPortfolioName.trim()) {
      alert('Please enter a portfolio name');
      return;
    }
    try {
      await addDoc(collection(db, 'customer_portfolios'), {
        name: newPortfolioName.trim(),
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        members: {
          [user.uid]: {
            role: 'customer_admin',
            joinedAt: serverTimestamp()
          }
        }
      });
      setNewPortfolioName('');
      setShowNewPortfolio(false);
    } catch (error) {
      alert('Failed to create portfolio: ' + error.message);
    }
  };

  const handleCreateBuilding = async () => {
    if (!newBuildingName.trim()) {
      alert('Please enter a building name');
      return;
    }
    try {
      await addDoc(collection(db, 'customer_portfolios', selectedPortfolio.id, 'buildings'), {
        name: newBuildingName.trim(),
        address: newBuildingAddress.trim(),
        created_at: serverTimestamp()
      });
      setNewBuildingName('');
      setNewBuildingAddress('');
      setShowNewBuilding(false);
    } catch (error) {
      alert('Failed to create building: ' + error.message);
    }
  };

  const handleAddSensor = async () => {
    if (!newSensorName.trim() || !selectedDevice || !selectedSensorId) {
      alert('Please enter sensor name, select a device, and select a physical sensor');
      return;
    }
    try {
      // NEW STRUCTURE: sensors are under devices, using sensor_id as doc ID
      const sensorRef = doc(db, 'customer_portfolios', selectedPortfolio.id, 'devices', selectedDevice, 'sensors', selectedSensorId);
      await setDoc(sensorRef, {
        name: newSensorName.trim(),
        sensor_id: selectedSensorId,
        threshold: 45,
        emailList: [],
        smsNumbers: [],
        created_at: serverTimestamp()
      });
      setNewSensorName('');
      setSelectedDevice('');
      setSelectedSensorId('');
      setShowNewSensor(false);
    } catch (error) {
      alert('Failed to add sensor: ' + error.message);
    }
  };

  const openSensorPage = (sensor) => {
    setViewingSensor(sensor);
    setEditSensorName(sensor.name || '');
    setEditThreshold(sensor.threshold || 45);
    setEditErrorDelay(sensor.errorDelayMinutes || 1);
    setEditContactUserIds(sensor.contactUserIds || []);
    setLegacySensorContacts(sensor.contacts || []);
    setContactPickerSearch('');
    setActiveTab('trends');
  };

  const closeSensorPage = () => {
    setViewingSensor(null);
    setActiveTab('settings');
  };

  const toggleContactUserId = (uid) => {
    setEditContactUserIds(prev =>
      prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );
  };

  const handleSaveSensorSettings = async () => {
    if (!viewingSensor || !viewingSensor.device_id) return;
    try {
      const sensorRef = doc(db, 'customer_portfolios', selectedPortfolio.id, 'devices', viewingSensor.device_id, 'sensors', viewingSensor.id);

      // Resolve selected user IDs against the loaded portfolio users so we
      // can derive the alert channels server.py reads.
      const selectedUsers = editContactUserIds
        .map(uid => portfolioUsers.find(u => u.uid === uid))
        .filter(Boolean);
      const { emailList, smsNumbers } = deriveAlertChannels(selectedUsers);

      await updateDoc(sensorRef, {
        name: editSensorName.trim() || viewingSensor.name,
        threshold: Number(editThreshold) || 45,
        errorDelayMinutes: Number(editErrorDelay) || 1,
        contactUserIds: editContactUserIds,
        emailList,
        smsNumbers,
        // Drop any legacy free-form contacts — the user-id list is now the
        // source of truth.
        contacts: []
      });

      setViewingSensor({
        ...viewingSensor,
        name: editSensorName.trim() || viewingSensor.name,
        threshold: Number(editThreshold) || 45,
        errorDelayMinutes: Number(editErrorDelay) || 1,
        contactUserIds: editContactUserIds,
        contacts: []
      });
      setLegacySensorContacts([]);

      alert('Settings saved!');
    } catch (error) {
      alert('Failed to save settings: ' + error.message);
    }
  };

  const handleDeleteSensor = async () => {
    if (!viewingSensor || !viewingSensor.device_id) return;
    if (!window.confirm(`Delete sensor "${viewingSensor.name}"? This cannot be undone.`)) return;
    try {
      // NEW STRUCTURE: sensors are under devices
      const sensorRef = doc(db, 'customer_portfolios', selectedPortfolio.id, 'devices', viewingSensor.device_id, 'sensors', viewingSensor.id);
      await deleteDoc(sensorRef);
      closeSensorPage();
    } catch (error) {
      alert('Failed to delete sensor: ' + error.message);
    }
  };

  const openDeviceSettings = (device) => {
    setEditingDevice(device);
    setEditDeviceName(device.name || '');
    setEditDeviceBuilding(device.building_id || '');
  };

  const handleSaveDevice = async () => {
    if (!editingDevice) return;
    try {
      const deviceRef = doc(db, 'customer_portfolios', selectedPortfolio.id, 'devices', editingDevice.id);
      const updateData = {
        name: editDeviceName.trim() || editingDevice.name
      };

      // Only update building_id if one is selected
      if (editDeviceBuilding) {
        updateData.building_id = editDeviceBuilding;
      }

      await updateDoc(deviceRef, updateData);
      setEditingDevice(null);
    } catch (error) {
      alert('Failed to save device: ' + error.message);
    }
  };

  const handleDeleteDevice = async () => {
    if (!editingDevice) return;
    if (!window.confirm(`Delete device "${editingDevice.name}"? This will NOT delete sensors assigned to this device, but they will no longer receive updates.`)) return;
    try {
      const deviceRef = doc(db, 'customer_portfolios', selectedPortfolio.id, 'devices', editingDevice.id);
      await deleteDoc(deviceRef);
      setEditingDevice(null);
    } catch (error) {
      alert('Failed to delete device: ' + error.message);
    }
  };

  // Save connection alert settings — connection alerts stay email-only,
  // so we ignore each user's smsCarrier when deriving recipients.
  const handleSaveConnectionSettings = async () => {
    if (!selectedPortfolio) return;
    setSavingConnectionSettings(true);
    try {
      const portfolioRef = doc(db, 'customer_portfolios', selectedPortfolio.id);
      const selectedUsers = connectionAlertUserIds
        .map(uid => portfolioUsers.find(u => u.uid === uid))
        .filter(Boolean);
      const emails = selectedUsers.map(u => u.email).filter(e => e);
      await updateDoc(portfolioRef, {
        connectionAlertsEnabled,
        offlineThresholdMinutes: Math.max(2, parseInt(offlineThresholdMinutes) || 20),
        connectionAlertUserIds,
        connectionAlertEmails: emails,
        // Drop legacy free-form contact list.
        connectionAlertContacts: []
      });
      setLegacyConnectionContacts([]);
      alert('Settings saved!');
    } catch (error) {
      alert('Failed to save connection settings: ' + error.message);
    } finally {
      setSavingConnectionSettings(false);
    }
  };

  const toggleConnectionAlertUserId = (uid) => {
    setConnectionAlertUserIds(prev =>
      prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );
  };

  // Compose the unique key used in bulkAssignments for a sensor row. We can't
  // use `sensor.id` alone because Firestore sensor doc IDs (the sensor_id) are
  // only unique within a device, not across the portfolio.
  const sensorKey = (s) => `${s.device_id}/${s.id}`;

  const openBulkAlerts = () => {
    const initial = {};
    sensors.forEach(s => {
      initial[sensorKey(s)] = [...(s.contactUserIds || [])];
    });
    setBulkAssignments(initial);
    // Deep-snapshot for diffing on save.
    setBulkOriginal(JSON.parse(JSON.stringify(initial)));
    setBulkSelectedUserIds([]);
    setBulkSelectedSensorKeys([]);
    setViewingBulkAlerts(true);
  };

  const closeBulkAlerts = () => {
    setViewingBulkAlerts(false);
  };

  const toggleBulkAssignment = (sensorK, uid) => {
    setBulkAssignments(prev => {
      const current = prev[sensorK] || [];
      const next = current.includes(uid)
        ? current.filter(x => x !== uid)
        : [...current, uid];
      return { ...prev, [sensorK]: next };
    });
  };

  const toggleBulkSelectedUser = (uid) => {
    setBulkSelectedUserIds(prev =>
      prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]
    );
  };

  const toggleBulkSelectedSensor = (sensorK) => {
    setBulkSelectedSensorKeys(prev =>
      prev.includes(sensorK) ? prev.filter(x => x !== sensorK) : [...prev, sensorK]
    );
  };

  const applyBulkAction = (action) => {
    if (bulkSelectedUserIds.length === 0 || bulkSelectedSensorKeys.length === 0) {
      alert('Select at least one user and one sensor.');
      return;
    }
    setBulkAssignments(prev => {
      const next = { ...prev };
      for (const sk of bulkSelectedSensorKeys) {
        const current = next[sk] || [];
        if (action === 'add') {
          const merged = Array.from(new Set([...current, ...bulkSelectedUserIds]));
          next[sk] = merged;
        } else {
          next[sk] = current.filter(uid => !bulkSelectedUserIds.includes(uid));
        }
      }
      return next;
    });
  };

  const saveBulkAlerts = async () => {
    if (!selectedPortfolio) return;
    setSavingBulk(true);
    try {
      const batch = writeBatch(db);
      let changed = 0;
      sensors.forEach(s => {
        const key = sensorKey(s);
        const before = bulkOriginal[key] || [];
        const after = bulkAssignments[key] || [];
        // Compare as sets — order doesn't matter.
        const sameLength = before.length === after.length;
        const sameMembers = sameLength && before.every(id => after.includes(id));
        if (sameMembers) return;

        const selectedUsers = after
          .map(uid => portfolioUsers.find(u => u.uid === uid))
          .filter(Boolean);
        const { emailList, smsNumbers } = deriveAlertChannels(selectedUsers);
        const sensorRef = doc(
          db,
          'customer_portfolios',
          selectedPortfolio.id,
          'devices',
          s.device_id,
          'sensors',
          s.id
        );
        batch.update(sensorRef, {
          contactUserIds: after,
          emailList,
          smsNumbers
        });
        changed++;
      });
      if (changed === 0) {
        alert('No changes to save.');
        setSavingBulk(false);
        return;
      }
      await batch.commit();
      // Re-snapshot original so further edits show the right diff.
      setBulkOriginal(JSON.parse(JSON.stringify(bulkAssignments)));
      alert(`Updated ${changed} sensor${changed === 1 ? '' : 's'}.`);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSavingBulk(false);
    }
  };

  // Bulk alarm-settings handlers. Working copy is keyed by sensorKey() and
  // holds { threshold, errorDelayMinutes } per sensor; bulkSettingsOriginal
  // is the snapshot at open time so saveBulkSettings can diff and only write
  // sensors that actually changed.
  const openBulkSettings = () => {
    const initial = {};
    sensors.forEach(s => {
      initial[sensorKey(s)] = {
        threshold: s.threshold ?? 45,
        errorDelayMinutes: s.errorDelayMinutes ?? 1
      };
    });
    setBulkSettings(initial);
    setBulkSettingsOriginal(JSON.parse(JSON.stringify(initial)));
    setBulkSettingsSelectedKeys([]);
    setBulkApplyThreshold(true);
    setBulkApplyErrorDelay(true);
    setBulkThresholdValue(45);
    setBulkErrorDelayValue(1);
    setViewingBulkSettings(true);
  };

  const closeBulkSettings = () => {
    setViewingBulkSettings(false);
  };

  const toggleBulkSettingsSelectedSensor = (sensorK) => {
    setBulkSettingsSelectedKeys(prev =>
      prev.includes(sensorK) ? prev.filter(x => x !== sensorK) : [...prev, sensorK]
    );
  };

  const updateBulkSensorField = (sensorK, field, value) => {
    setBulkSettings(prev => ({
      ...prev,
      [sensorK]: { ...(prev[sensorK] || {}), [field]: value }
    }));
  };

  const applyBulkSettingsAction = () => {
    if (bulkSettingsSelectedKeys.length === 0) {
      alert('Select at least one sensor.');
      return;
    }
    if (!bulkApplyThreshold && !bulkApplyErrorDelay) {
      alert('Pick at least one setting to apply.');
      return;
    }
    const threshold = Number(bulkThresholdValue);
    const errorDelay = Number(bulkErrorDelayValue);
    if (bulkApplyThreshold && !Number.isFinite(threshold)) {
      alert('Enter a valid temperature threshold.');
      return;
    }
    if (bulkApplyErrorDelay && (!Number.isFinite(errorDelay) || errorDelay < 1)) {
      alert('Enter a valid error delay (minutes).');
      return;
    }
    setBulkSettings(prev => {
      const next = { ...prev };
      for (const sk of bulkSettingsSelectedKeys) {
        const current = next[sk] || {};
        next[sk] = {
          threshold: bulkApplyThreshold ? threshold : current.threshold,
          errorDelayMinutes: bulkApplyErrorDelay ? errorDelay : current.errorDelayMinutes
        };
      }
      return next;
    });
  };

  const saveBulkSettings = async () => {
    if (!selectedPortfolio) return;
    setSavingBulkSettings(true);
    try {
      const batch = writeBatch(db);
      let changed = 0;
      sensors.forEach(s => {
        const key = sensorKey(s);
        const before = bulkSettingsOriginal[key] || {};
        const after = bulkSettings[key] || {};
        const thresholdAfter = Number(after.threshold);
        const errorAfter = Number(after.errorDelayMinutes);
        if (!Number.isFinite(thresholdAfter) || !Number.isFinite(errorAfter) || errorAfter < 1) {
          return;
        }
        const sameThreshold = Number(before.threshold) === thresholdAfter;
        const sameError = Number(before.errorDelayMinutes) === errorAfter;
        if (sameThreshold && sameError) return;

        const sensorRef = doc(
          db,
          'customer_portfolios',
          selectedPortfolio.id,
          'devices',
          s.device_id,
          'sensors',
          s.id
        );
        batch.update(sensorRef, {
          threshold: thresholdAfter,
          errorDelayMinutes: errorAfter
        });
        changed++;
      });
      if (changed === 0) {
        alert('No changes to save.');
        setSavingBulkSettings(false);
        return;
      }
      await batch.commit();
      setBulkSettingsOriginal(JSON.parse(JSON.stringify(bulkSettings)));
      alert(`Updated ${changed} sensor${changed === 1 ? '' : 's'}.`);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSavingBulkSettings(false);
    }
  };

  // Team management functions
  const handleInviteUser = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      alert('Please enter an email address');
      return;
    }

    setInviteLoading(true);
    try {
      // Check if user already exists
      const usersSnapshot = await getDocs(collection(db, 'users'));
      let existingUser = null;
      usersSnapshot.forEach((doc) => {
        if (doc.data().email?.toLowerCase() === email) {
          existingUser = { uid: doc.id, ...doc.data() };
        }
      });

      if (existingUser) {
        // User exists - check if already a member
        if (selectedPortfolio.members?.[existingUser.uid]) {
          alert(`${email} is already a member of this portfolio`);
          setInviteLoading(false);
          return;
        }

        // Add existing user to portfolio
        const portfolioRef = doc(db, 'customer_portfolios', selectedPortfolio.id);
        const newMembers = {
          ...selectedPortfolio.members,
          [existingUser.uid]: {
            role: inviteRole,
            joinedAt: serverTimestamp()
          }
        };
        await updateDoc(portfolioRef, { members: newMembers });

        // Update user's portfolioIds
        const userRef = doc(db, 'users', existingUser.uid);
        await updateDoc(userRef, {
          portfolioIds: arrayUnion(selectedPortfolio.id)
        });

        // Update local state
        setSelectedPortfolio({ ...selectedPortfolio, members: newMembers });
        setTeamMembers([...teamMembers, {
          uid: existingUser.uid,
          name: existingUser.name || existingUser.email,
          email: existingUser.email,
          role: inviteRole
        }]);

        alert(`${existingUser.name || email} has been added to the portfolio as ${getRoleDisplayName(inviteRole)}`);
      } else {
        // User doesn't exist - create new account
        // Generate random password
        const tempPassword = Math.random().toString(36).slice(-12) + 'A1!';

        // Create user via Firebase Auth REST API
        const response = await fetch(
          `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${firebaseConfig.apiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: email,
              password: tempPassword,
              returnSecureToken: false
            })
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error?.message || 'Failed to create user');
        }

        // Create user document in Firestore
        await setDoc(doc(db, 'users', data.localId), {
          email: email,
          name: null,
          role: null,
          portfolioIds: [selectedPortfolio.id],
          createdAt: serverTimestamp(),
          createdBy: user.uid
        });

        // Add to portfolio
        const portfolioRef = doc(db, 'customer_portfolios', selectedPortfolio.id);
        const newMembers = {
          ...selectedPortfolio.members,
          [data.localId]: {
            role: inviteRole,
            joinedAt: serverTimestamp()
          }
        };
        await updateDoc(portfolioRef, { members: newMembers });

        // Send welcome/password reset email
        await fetch(
          `https://identitytoolkit.googleapis.com/v1/accounts:sendOobCode?key=${firebaseConfig.apiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              requestType: 'PASSWORD_RESET',
              email: email
            })
          }
        );

        // Update local state
        setSelectedPortfolio({ ...selectedPortfolio, members: newMembers });
        setTeamMembers([...teamMembers, {
          uid: data.localId,
          name: email,
          email: email,
          role: inviteRole
        }]);

        alert(`Account created for ${email} and welcome email sent! They've been added as ${getRoleDisplayName(inviteRole)}.`);
      }

      setInviteEmail('');
      setInviteRole('customer_viewer');
    } catch (error) {
      let message = error.message;
      if (message.includes('EMAIL_EXISTS')) {
        message = 'This email is already registered but not in our user database. Contact an administrator.';
      } else if (message.includes('INVALID_EMAIL')) {
        message = 'Invalid email address';
      }
      alert('Failed to invite user: ' + message);
    }
    setInviteLoading(false);
  };

  const handleRemoveMember = async (memberUid) => {
    if (memberUid === user.uid) {
      alert("You can't remove yourself from the portfolio");
      return;
    }

    const member = teamMembers.find(m => m.uid === memberUid);
    if (!window.confirm(`Remove ${member?.name || member?.email} from this portfolio?`)) return;

    try {
      const portfolioRef = doc(db, 'customer_portfolios', selectedPortfolio.id);
      const newMembers = { ...selectedPortfolio.members };
      delete newMembers[memberUid];

      await updateDoc(portfolioRef, { members: newMembers });

      // Update local state
      setSelectedPortfolio({ ...selectedPortfolio, members: newMembers });
      setTeamMembers(teamMembers.filter(m => m.uid !== memberUid));
    } catch (error) {
      alert('Failed to remove member: ' + error.message);
    }
  };

  const handleChangeRole = async (memberUid, newRole) => {
    if (memberUid === user.uid) {
      alert("You can't change your own role");
      return;
    }

    try {
      const portfolioRef = doc(db, 'customer_portfolios', selectedPortfolio.id);
      const newMembers = {
        ...selectedPortfolio.members,
        [memberUid]: {
          ...selectedPortfolio.members[memberUid],
          role: newRole
        }
      };

      await updateDoc(portfolioRef, { members: newMembers });

      // Update local state
      setSelectedPortfolio({ ...selectedPortfolio, members: newMembers });
      setTeamMembers(teamMembers.map(m =>
        m.uid === memberUid ? { ...m, role: newRole } : m
      ));
    } catch (error) {
      alert('Failed to change role: ' + error.message);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Team Panel
  if (showTeamPanel && selectedPortfolio) {
    const invitableRoles = getInvitableRoles(selectedPortfolio.members || {});

    return (
      <div className="team-panel">
        <div className="team-header">
          <button className="back-btn" onClick={() => setShowTeamPanel(false)}>← Back</button>
          <h1>Team - {selectedPortfolio.name}</h1>
        </div>

        <div className="team-content">
          {canManageUsers && invitableRoles.length > 0 && (
            <div className="invite-section">
              <h3>Invite New Member</h3>
              <div className="invite-form">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Email address"
                />
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                  {invitableRoles.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <button
                  className="btn-primary"
                  onClick={handleInviteUser}
                  disabled={inviteLoading}
                >
                  {inviteLoading ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </div>
          )}

          <div className="members-section">
            <h3>Members ({teamMembers.length})</h3>
            <div className="members-list">
              {teamMembers.map(member => (
                <div key={member.uid} className="member-card">
                  <div className="member-info">
                    <div className="member-name">
                      {member.name}
                      {member.uid === user.uid && <span className="you-badge">You</span>}
                    </div>
                    <div className="member-email">{member.email}</div>
                  </div>
                  <div className="member-role">
                    {canManageUsers && member.uid !== user.uid ? (
                      <select
                        value={member.role}
                        onChange={(e) => handleChangeRole(member.uid, e.target.value)}
                      >
                        {isSystemAdmin() && <option value="customer_admin">Admin</option>}
                        <option value="customer_technician">Contractor</option>
                        <option value="customer_viewer">Viewer</option>
                      </select>
                    ) : (
                      <span className={`role-tag ${member.role}`}>
                        {getRoleDisplayName(member.role)}
                      </span>
                    )}
                  </div>
                  {canManageUsers && member.uid !== user.uid && (
                    <button
                      className="btn-remove-small"
                      onClick={() => handleRemoveMember(member.uid)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <style>{`
          .team-panel {
            height: calc(100vh - 60px);
            background: #16213e;
            display: flex;
            flex-direction: column;
          }
          .team-header {
            display: flex;
            align-items: center;
            gap: 20px;
            padding: 20px;
            background: #1a1a2e;
            border-bottom: 1px solid #3a3a5a;
          }
          .team-header h1 {
            margin: 0;
            color: #fff;
          }
          .team-content {
            flex: 1;
            overflow-y: auto;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
            width: 100%;
          }
          .invite-section {
            background: #1a1a2e;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 30px;
          }
          .invite-section h3 {
            margin: 0 0 15px 0;
            color: #4ecdc4;
          }
          .invite-form {
            display: flex;
            gap: 10px;
          }
          .invite-form input {
            flex: 1;
            padding: 10px;
            border: 1px solid #3a3a5a;
            border-radius: 4px;
            background: #16213e;
            color: #fff;
          }
          .invite-form select {
            padding: 10px;
            border: 1px solid #3a3a5a;
            border-radius: 4px;
            background: #16213e;
            color: #fff;
          }
          .members-section h3 {
            margin: 0 0 15px 0;
            color: #4ecdc4;
          }
          .members-list {
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .member-card {
            display: flex;
            align-items: center;
            background: #1a1a2e;
            border-radius: 8px;
            padding: 15px;
            gap: 15px;
          }
          .member-info {
            flex: 1;
          }
          .member-name {
            font-weight: 600;
            color: #fff;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .member-email {
            font-size: 13px;
            color: #888;
          }
          .you-badge {
            background: #4ecdc4;
            color: #1a1a2e;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 10px;
            font-weight: 600;
          }
          .member-role select {
            padding: 8px;
            border: 1px solid #3a3a5a;
            border-radius: 4px;
            background: #16213e;
            color: #fff;
          }
          .role-tag {
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
          }
          .role-tag.customer_admin {
            background: #3498db;
            color: #fff;
          }
          .role-tag.customer_technician {
            background: #2ecc71;
            color: #fff;
          }
          .role-tag.customer_viewer {
            background: #95a5a6;
            color: #fff;
          }
          .btn-remove-small {
            background: #e74c3c;
            color: #fff;
            border: none;
            padding: 8px 12px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
          }
          .btn-remove-small:hover {
            background: #c0392b;
          }
        `}</style>
      </div>
    );
  }

  // Bulk Alert Contacts Page View — pick a set of users + a set of sensors
  // and add/remove the users to all selected sensors at once. Also lets you
  // toggle individual cells in the matrix for fine-grained edits.
  if (viewingBulkAlerts && selectedPortfolio) {
    const sensorsByBuilding = {};
    sensors.forEach(s => {
      const building = buildings.find(b => b.id === s.building_id);
      const bName = building?.name || 'Unassigned';
      if (!sensorsByBuilding[bName]) sensorsByBuilding[bName] = [];
      sensorsByBuilding[bName].push(s);
    });
    const buildingNames = Object.keys(sensorsByBuilding).sort();
    const totalChanged = sensors.reduce((acc, s) => {
      const k = sensorKey(s);
      const before = bulkOriginal[k] || [];
      const after = bulkAssignments[k] || [];
      const same = before.length === after.length && before.every(id => after.includes(id));
      return acc + (same ? 0 : 1);
    }, 0);

    return (
      <div className="sensor-page bulk-alerts-page">
        <div className="sensor-page-header">
          <button className="back-btn" onClick={closeBulkAlerts}>← Back</button>
          <h1>Bulk Alert Contacts</h1>
          <span className="bulk-changed-badge">
            {totalChanged === 0 ? 'No changes' : `${totalChanged} sensor${totalChanged === 1 ? '' : 's'} changed`}
          </span>
        </div>

        <div className="sensor-page-content">
          <div className="bulk-intro">
            <p className="hint">
              Tick checkboxes in the matrix below to assign portfolio members
              as alert contacts for each sensor. Or use the bulk action bar to
              add or remove a set of users from a set of sensors in one step.
            </p>
          </div>

          {portfolioUsers.length === 0 ? (
            <p className="hint">
              No portfolio members yet — invite users from the Team panel before
              assigning alert contacts.
            </p>
          ) : sensors.length === 0 ? (
            <p className="hint">No sensors in this portfolio yet.</p>
          ) : (
            <>
              <div className="bulk-action-bar">
                <div className="bulk-action-section">
                  <div className="bulk-action-label">
                    Selected users ({bulkSelectedUserIds.length})
                  </div>
                  <div className="bulk-chip-row">
                    {portfolioUsers.map(u => {
                      const sel = bulkSelectedUserIds.includes(u.uid);
                      return (
                        <button
                          key={u.uid}
                          className={`bulk-chip ${sel ? 'selected' : ''}`}
                          onClick={() => toggleBulkSelectedUser(u.uid)}
                        >
                          {u.name}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="bulk-action-section">
                  <div className="bulk-action-label">
                    Selected sensors ({bulkSelectedSensorKeys.length} of {sensors.length})
                  </div>
                  <div className="bulk-action-buttons">
                    <button
                      className="btn-small"
                      onClick={() => setBulkSelectedSensorKeys(sensors.map(sensorKey))}
                    >
                      Select all sensors
                    </button>
                    <button
                      className="btn-small"
                      onClick={() => setBulkSelectedSensorKeys([])}
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="bulk-action-buttons">
                  <button className="btn-primary" onClick={() => applyBulkAction('add')}>
                    Add to selected sensors
                  </button>
                  <button className="btn-secondary" onClick={() => applyBulkAction('remove')}>
                    Remove from selected sensors
                  </button>
                </div>
              </div>

              <div className="bulk-matrix-wrap">
                <table className="bulk-matrix">
                  <thead>
                    <tr>
                      <th className="sensor-header">Sensor</th>
                      {portfolioUsers.map(u => (
                        <th key={u.uid} className="user-header">
                          <div className="user-header-name">{u.name}</div>
                          <div className={`user-header-sms ${u.phone && u.smsCarrier ? 'ready' : 'missing'}`}>
                            {u.phone && u.smsCarrier ? 'SMS ready' : 'no SMS'}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {buildingNames.map(bName => (
                      <React.Fragment key={bName}>
                        <tr className="building-row">
                          <td colSpan={portfolioUsers.length + 1}>{bName}</td>
                        </tr>
                        {sensorsByBuilding[bName].map(s => {
                          const k = sensorKey(s);
                          const sensorSelected = bulkSelectedSensorKeys.includes(k);
                          const before = bulkOriginal[k] || [];
                          const after = bulkAssignments[k] || [];
                          const isDirty =
                            before.length !== after.length ||
                            !before.every(id => after.includes(id));
                          return (
                            <tr key={k} className={isDirty ? 'dirty' : ''}>
                              <td className="sensor-cell">
                                <label className="sensor-cell-label">
                                  <input
                                    type="checkbox"
                                    checked={sensorSelected}
                                    onChange={() => toggleBulkSelectedSensor(k)}
                                  />
                                  <span className="sensor-name">{s.name || s.id}</span>
                                  <span className="sensor-device">{s.device_name || s.device_id}</span>
                                </label>
                              </td>
                              {portfolioUsers.map(u => {
                                const checked = (bulkAssignments[k] || []).includes(u.uid);
                                return (
                                  <td key={u.uid} className="cell">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => toggleBulkAssignment(k, u.uid)}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="form-actions" style={{ marginTop: '24px' }}>
                <button
                  className="btn-primary"
                  onClick={saveBulkAlerts}
                  disabled={savingBulk || totalChanged === 0}
                >
                  {savingBulk ? 'Saving...' : `Save changes (${totalChanged})`}
                </button>
                <button className="btn-secondary" onClick={closeBulkAlerts}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>

        <style>{`
          .bulk-alerts-page .sensor-page-content { padding: 24px; }
          .bulk-changed-badge {
            background: #3a3a5a;
            color: #4ecdc4;
            padding: 6px 12px;
            border-radius: 14px;
            font-size: 13px;
            margin-left: 12px;
          }
          .bulk-intro { max-width: 900px; margin-bottom: 16px; }
          .bulk-action-bar {
            display: flex;
            flex-direction: column;
            gap: 14px;
            background: #1a1a2e;
            border: 1px solid #2a2a4a;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 20px;
          }
          .bulk-action-section { display: flex; flex-direction: column; gap: 8px; }
          .bulk-action-label { color: #888; font-size: 13px; }
          .bulk-chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
          .bulk-chip {
            background: #2a2a4a;
            border: 1px solid #3a3a5a;
            color: #fff;
            padding: 6px 12px;
            border-radius: 14px;
            cursor: pointer;
            font-size: 13px;
          }
          .bulk-chip.selected {
            background: #4ecdc4;
            color: #1a1a2e;
            border-color: #4ecdc4;
          }
          .bulk-action-buttons { display: flex; gap: 10px; flex-wrap: wrap; }
          .btn-small {
            background: #3a3a5a;
            color: #fff;
            border: none;
            padding: 8px 14px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 13px;
          }
          .btn-small:hover { background: #4a4a6a; }
          .bulk-matrix-wrap {
            overflow-x: auto;
            background: #1a1a2e;
            border: 1px solid #2a2a4a;
            border-radius: 8px;
          }
          .bulk-matrix { width: 100%; border-collapse: collapse; }
          .bulk-matrix th, .bulk-matrix td {
            padding: 10px 12px;
            border-bottom: 1px solid #2a2a4a;
            text-align: left;
          }
          .bulk-matrix th {
            background: #16213e;
            color: #4ecdc4;
            font-size: 13px;
            position: sticky;
            top: 0;
          }
          .bulk-matrix .sensor-header { min-width: 240px; }
          .bulk-matrix .user-header { text-align: center; min-width: 110px; }
          .bulk-matrix .user-header-name { font-weight: 600; color: #fff; }
          .bulk-matrix .user-header-sms { font-size: 11px; margin-top: 2px; }
          .bulk-matrix .user-header-sms.ready { color: #4ecdc4; }
          .bulk-matrix .user-header-sms.missing { color: #f39c12; }
          .bulk-matrix tr.dirty { background: rgba(78, 205, 196, 0.05); }
          .bulk-matrix tr.dirty .sensor-cell { border-left: 3px solid #4ecdc4; }
          .bulk-matrix tr.building-row td {
            background: #20284a;
            color: #4ecdc4;
            font-weight: 600;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            padding: 8px 12px;
          }
          .sensor-cell-label {
            display: flex;
            align-items: center;
            gap: 10px;
            cursor: pointer;
          }
          .sensor-cell input[type="checkbox"] {
            width: 16px;
            height: 16px;
            accent-color: #4ecdc4;
          }
          .sensor-name { color: #fff; font-weight: 500; }
          .sensor-device { color: #888; font-size: 11px; margin-left: 4px; }
          .bulk-matrix .cell { text-align: center; }
          .bulk-matrix .cell input[type="checkbox"] {
            width: 18px;
            height: 18px;
            cursor: pointer;
            accent-color: #4ecdc4;
          }
          .btn-bulk-alerts {
            background: #9b59b6;
            color: #fff;
            border: none;
            padding: 8px 14px;
            border-radius: 4px;
            cursor: pointer;
            font-weight: 600;
          }
          .btn-bulk-alerts:hover { background: #8e44ad; }
        `}</style>
      </div>
    );
  }

  // Bulk Alarm Settings Page View — pick a set of sensors and apply a
  // temperature threshold and/or sensor-error delay to all of them. Mirrors
  // the Bulk Alert Contacts page so the two share a visual language.
  if (viewingBulkSettings && selectedPortfolio) {
    const sensorsByBuilding = {};
    sensors.forEach(s => {
      const building = buildings.find(b => b.id === s.building_id);
      const bName = building?.name || 'Unassigned';
      if (!sensorsByBuilding[bName]) sensorsByBuilding[bName] = [];
      sensorsByBuilding[bName].push(s);
    });
    const buildingNames = Object.keys(sensorsByBuilding).sort();
    const totalChanged = sensors.reduce((acc, s) => {
      const k = sensorKey(s);
      const before = bulkSettingsOriginal[k] || {};
      const after = bulkSettings[k] || {};
      const same =
        Number(before.threshold) === Number(after.threshold) &&
        Number(before.errorDelayMinutes) === Number(after.errorDelayMinutes);
      return acc + (same ? 0 : 1);
    }, 0);
    const appliedCount = (bulkApplyThreshold ? 1 : 0) + (bulkApplyErrorDelay ? 1 : 0);

    const resetSelectedSensors = () => {
      if (bulkSettingsSelectedKeys.length === 0) {
        alert('Select at least one sensor.');
        return;
      }
      setBulkSettings(prev => {
        const next = { ...prev };
        for (const sk of bulkSettingsSelectedKeys) {
          next[sk] = { ...(bulkSettingsOriginal[sk] || {}) };
        }
        return next;
      });
    };

    return (
      <div className="sensor-page bulk-alerts-page">
        <div className="sensor-page-header">
          <button className="back-btn" onClick={closeBulkSettings}>← Back</button>
          <h1>Bulk Alarm Settings</h1>
          <span className="bulk-changed-badge">
            {totalChanged === 0 ? 'No changes' : `${totalChanged} sensor${totalChanged === 1 ? '' : 's'} changed`}
          </span>
        </div>

        <div className="sensor-page-content">
          <div className="bulk-intro">
            <p className="hint">
              Pick the settings you want to apply and the sensors to apply them
              to, then click "Apply to selected sensors". You can also edit any
              row directly. Nothing is saved until you click Save.
            </p>
          </div>

          {sensors.length === 0 ? (
            <p className="hint">No sensors in this portfolio yet.</p>
          ) : (
            <>
              <div className="bulk-action-bar">
                <div className="bulk-action-section">
                  <div className="bulk-action-label">
                    Settings to apply ({appliedCount})
                  </div>
                  <div className="bulk-chip-row">
                    <div className="setting-row">
                      <button
                        type="button"
                        className={`bulk-chip ${bulkApplyThreshold ? 'selected' : ''}`}
                        onClick={() => setBulkApplyThreshold(!bulkApplyThreshold)}
                      >
                        Threshold
                      </button>
                      <div className={`setting-value-box ${!bulkApplyThreshold ? 'dim' : ''}`}>
                        <input
                          type="number"
                          className="setting-value-input"
                          value={bulkThresholdValue}
                          onChange={(e) => setBulkThresholdValue(e.target.value)}
                          min="0"
                          max="200"
                          disabled={!bulkApplyThreshold}
                        />
                        <span className="setting-value-unit">°F</span>
                      </div>
                    </div>
                    <div className="setting-row">
                      <button
                        type="button"
                        className={`bulk-chip ${bulkApplyErrorDelay ? 'selected' : ''}`}
                        onClick={() => setBulkApplyErrorDelay(!bulkApplyErrorDelay)}
                      >
                        Error Delay
                      </button>
                      <div className={`setting-value-box ${!bulkApplyErrorDelay ? 'dim' : ''}`}>
                        <input
                          type="number"
                          className="setting-value-input"
                          value={bulkErrorDelayValue}
                          onChange={(e) => setBulkErrorDelayValue(e.target.value)}
                          min="1"
                          max="60"
                          disabled={!bulkApplyErrorDelay}
                        />
                        <span className="setting-value-unit">min</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bulk-action-section">
                  <div className="bulk-action-label">
                    Selected sensors ({bulkSettingsSelectedKeys.length} of {sensors.length})
                  </div>
                  <div className="bulk-action-buttons">
                    <button
                      className="btn-small"
                      onClick={() => setBulkSettingsSelectedKeys(sensors.map(sensorKey))}
                    >
                      Select all sensors
                    </button>
                    <button
                      className="btn-small"
                      onClick={() => setBulkSettingsSelectedKeys([])}
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="bulk-action-buttons">
                  <button className="btn-primary apply-btn" onClick={applyBulkSettingsAction}>
                    Apply to selected sensors
                  </button>
                  <button className="btn-secondary" onClick={resetSelectedSensors}>
                    Reset selected sensors
                  </button>
                </div>
              </div>

              <div className="bulk-matrix-wrap">
                <table className="bulk-matrix bulk-settings-matrix">
                  <thead>
                    <tr>
                      <th className="sensor-header">Sensor</th>
                      <th className="setting-col-header">
                        <div className="setting-col-name">Threshold</div>
                        <div className="setting-col-unit">°F</div>
                      </th>
                      <th className="setting-col-header">
                        <div className="setting-col-name">Error Delay</div>
                        <div className="setting-col-unit">min</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {buildingNames.map(bName => (
                      <React.Fragment key={bName}>
                        <tr className="building-row">
                          <td colSpan={3}>{bName}</td>
                        </tr>
                        {sensorsByBuilding[bName].map(s => {
                          const k = sensorKey(s);
                          const sensorSelected = bulkSettingsSelectedKeys.includes(k);
                          const before = bulkSettingsOriginal[k] || {};
                          const after = bulkSettings[k] || {};
                          const isDirty =
                            Number(before.threshold) !== Number(after.threshold) ||
                            Number(before.errorDelayMinutes) !== Number(after.errorDelayMinutes);
                          return (
                            <tr key={k} className={isDirty ? 'dirty' : ''}>
                              <td className="sensor-cell">
                                <label className="sensor-cell-label">
                                  <input
                                    type="checkbox"
                                    checked={sensorSelected}
                                    onChange={() => toggleBulkSettingsSelectedSensor(k)}
                                  />
                                  <span className="sensor-name">{s.name || s.id}</span>
                                  <span className="sensor-device">{s.device_name || s.device_id}</span>
                                </label>
                              </td>
                              <td className="cell">
                                <input
                                  type="number"
                                  className="row-input"
                                  value={after.threshold ?? ''}
                                  onChange={(e) => updateBulkSensorField(k, 'threshold', e.target.value)}
                                  min="0"
                                  max="200"
                                />
                              </td>
                              <td className="cell">
                                <input
                                  type="number"
                                  className="row-input"
                                  value={after.errorDelayMinutes ?? ''}
                                  onChange={(e) => updateBulkSensorField(k, 'errorDelayMinutes', e.target.value)}
                                  min="1"
                                  max="60"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="form-actions" style={{ marginTop: '24px' }}>
                <button
                  className="btn-primary"
                  onClick={saveBulkSettings}
                  disabled={savingBulkSettings || totalChanged === 0}
                >
                  {savingBulkSettings ? 'Saving...' : `Save changes (${totalChanged})`}
                </button>
                <button className="btn-secondary" onClick={closeBulkSettings}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>

        <style>{`
          /* Shared bulk-page styles (must be present whenever this page is
             rendered — they live alongside the contacts page styles, so we
             duplicate them here so the Settings page picks them up too). */
          .bulk-alerts-page .sensor-page-content { padding: 24px; }
          .bulk-changed-badge {
            background: #3a3a5a;
            color: #4ecdc4;
            padding: 6px 12px;
            border-radius: 14px;
            font-size: 13px;
            margin-left: 12px;
          }
          .bulk-intro { max-width: 900px; margin-bottom: 16px; }
          .bulk-action-bar {
            display: flex;
            flex-direction: column;
            gap: 14px;
            background: #1a1a2e;
            border: 1px solid #2a2a4a;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 20px;
          }
          .bulk-action-section { display: flex; flex-direction: column; gap: 8px; }
          .bulk-action-label { color: #888; font-size: 13px; }
          .bulk-chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
          .bulk-chip {
            background: #2a2a4a;
            border: 1px solid #3a3a5a;
            color: #fff;
            padding: 6px 12px;
            border-radius: 14px;
            cursor: pointer;
            font-size: 13px;
          }
          .bulk-chip.selected {
            background: #4ecdc4;
            color: #1a1a2e;
            border-color: #4ecdc4;
          }
          .bulk-action-buttons { display: flex; gap: 10px; flex-wrap: wrap; }
          .btn-small {
            background: #3a3a5a;
            color: #fff;
            border: none;
            padding: 8px 14px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 13px;
          }
          .btn-small:hover { background: #4a4a6a; }
          .bulk-matrix-wrap {
            overflow-x: auto;
            background: #1a1a2e;
            border: 1px solid #2a2a4a;
            border-radius: 8px;
          }
          .bulk-matrix { width: 100%; border-collapse: collapse; }
          .bulk-matrix th, .bulk-matrix td {
            padding: 10px 12px;
            border-bottom: 1px solid #2a2a4a;
            text-align: left;
          }
          .bulk-matrix th {
            background: #16213e;
            color: #4ecdc4;
            font-size: 13px;
            position: sticky;
            top: 0;
          }
          .bulk-matrix .sensor-header { min-width: 240px; }
          .bulk-matrix tr.dirty { background: rgba(78, 205, 196, 0.05); }
          .bulk-matrix tr.dirty .sensor-cell { border-left: 3px solid #4ecdc4; }
          .bulk-matrix tr.building-row td {
            background: #20284a;
            color: #4ecdc4;
            font-weight: 600;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            padding: 8px 12px;
          }
          .sensor-cell-label {
            display: flex;
            align-items: center;
            gap: 10px;
            cursor: pointer;
          }
          .sensor-cell input[type="checkbox"] {
            width: 16px;
            height: 16px;
            accent-color: #4ecdc4;
          }
          .sensor-name { color: #fff; font-weight: 500; }
          .sensor-device { color: #888; font-size: 11px; margin-left: 4px; }
          .bulk-matrix .cell { text-align: center; }

          /* Each setting pairs a plain .bulk-chip toggle with a separate
             dark value box. The chip is identical to a user chip; the value
             box sits next to it, dimmed when the chip is off. */
          .setting-row {
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }
          .setting-value-box {
            display: inline-flex;
            align-items: center;
            background: #2a2a4a;
            border: 1px solid #3a3a5a;
            border-radius: 4px;
            overflow: hidden;
            height: 28px;
          }
          .setting-value-box.dim { opacity: 0.45; }
          .setting-value-input {
            background: transparent;
            border: none;
            color: #fff;
            padding: 0 2px 0 10px;
            width: 44px;
            font-size: 13px;
            font-weight: 600;
            text-align: right;
            font-variant-numeric: tabular-nums;
          }
          .setting-value-input:focus { outline: none; }
          .setting-value-input:disabled { cursor: not-allowed; }
          .setting-value-input::-webkit-outer-spin-button,
          .setting-value-input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          .setting-value-unit {
            color: #888;
            font-size: 12px;
            padding: 0 10px 0 4px;
          }

          /* Apply button row — primary stretches wide, secondary keeps its
             natural width, exactly mirroring "Add to selected sensors" /
             "Remove from selected sensors" on the contacts page. */
          .apply-btn { flex: 1; }

          /* Matrix headers — column name on top, unit below in dim gray,
             echoing "Karl Pierson / no SMS" on the contacts page. */
          .bulk-settings-matrix .setting-col-header {
            text-align: center;
            min-width: 160px;
          }
          .bulk-settings-matrix .setting-col-name {
            color: #fff;
            font-weight: 600;
            font-size: 13px;
          }
          .bulk-settings-matrix .setting-col-unit {
            color: #888;
            font-size: 11px;
            margin-top: 2px;
            font-weight: 500;
          }
          .bulk-settings-matrix .cell { text-align: center; vertical-align: middle; }
          .row-input {
            background: #2a2a4a;
            border: 1px solid #3a3a5a;
            color: #fff;
            padding: 5px 8px;
            border-radius: 4px;
            width: 80px;
            text-align: center;
            font-size: 13px;
            font-weight: 600;
            font-variant-numeric: tabular-nums;
          }
          .row-input:focus { outline: none; border-color: #4ecdc4; }
          .bulk-settings-matrix tr.dirty .row-input {
            border-color: #4ecdc4;
            background: rgba(78, 205, 196, 0.12);
          }
        `}</style>
      </div>
    );
  }

  // Connection Alerts Page View
  if (viewingConnectionAlerts && selectedPortfolio) {
    const onlineDevices = devices.filter(d => getDeviceStatus(d, offlineThresholdMinutes).status === 'online');
    const offlineDevices = devices.filter(d => getDeviceStatus(d, offlineThresholdMinutes).status === 'offline');
    const unknownDevices = devices.filter(d => getDeviceStatus(d, offlineThresholdMinutes).status === 'unknown');

    return (
      <div className="sensor-page connection-alerts-page">
        <div className="sensor-page-header">
          <button className="back-btn" onClick={() => setViewingConnectionAlerts(false)}>← Back</button>
          <h1>Connection Alerts</h1>
          <div className="connection-status-summary">
            <span className="status-badge online">{onlineDevices.length} Online</span>
            <span className="status-badge offline">{offlineDevices.length} Offline</span>
          </div>
        </div>

        <div className="sensor-page-tabs">
          <button
            className={`tab ${connectionAlertsTab === 'status' ? 'active' : ''}`}
            onClick={() => setConnectionAlertsTab('status')}
          >
            Device Status
          </button>
          <button
            className={`tab ${connectionAlertsTab === 'logs' ? 'active' : ''}`}
            onClick={() => setConnectionAlertsTab('logs')}
          >
            Event Log
          </button>
          {canEdit && (
            <>
              <button
                className={`tab ${connectionAlertsTab === 'contacts' ? 'active' : ''}`}
                onClick={() => setConnectionAlertsTab('contacts')}
              >
                Contacts
              </button>
              <button
                className={`tab ${connectionAlertsTab === 'settings' ? 'active' : ''}`}
                onClick={() => setConnectionAlertsTab('settings')}
              >
                Settings
              </button>
            </>
          )}
        </div>

        <div className="sensor-page-content">
          {connectionAlertsTab === 'status' && (
            <div className="status-panel">
              {offlineDevices.length > 0 && (
                <div className="status-section offline-section">
                  <h3>Offline Devices ({offlineDevices.length})</h3>
                  <div className="device-status-list">
                    {offlineDevices.map(d => {
                      const status = getDeviceStatus(d, offlineThresholdMinutes);
                      return (
                        <div key={d.id} className="device-status-card offline">
                          <span className="status-dot offline"></span>
                          <div className="device-status-info">
                            <span className="device-name">{d.name || d.id}</span>
                            <span className="device-serial">{d.serial || 'No serial'}</span>
                          </div>
                          <WifiSignal signal={d.wifi_signal} offline={status.status === 'offline'} />
                          <div className="device-last-seen">
                            Last seen: {status.lastSeenText}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {onlineDevices.length > 0 && (
                <div className="status-section online-section">
                  <h3>Online Devices ({onlineDevices.length})</h3>
                  <div className="device-status-list">
                    {onlineDevices.map(d => {
                      const status = getDeviceStatus(d, offlineThresholdMinutes);
                      return (
                        <div key={d.id} className="device-status-card online">
                          <span className="status-dot online"></span>
                          <div className="device-status-info">
                            <span className="device-name">{d.name || d.id}</span>
                            <span className="device-serial">{d.serial || 'No serial'}</span>
                          </div>
                          <WifiSignal signal={d.wifi_signal} offline={status.status === 'offline'} />
                          <div className="device-last-seen">
                            Last seen: {status.lastSeenText}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {unknownDevices.length > 0 && (
                <div className="status-section">
                  <h3>Unknown Status ({unknownDevices.length})</h3>
                  <div className="device-status-list">
                    {unknownDevices.map(d => (
                      <div key={d.id} className="device-status-card unknown">
                        <span className="status-dot unknown"></span>
                        <div className="device-status-info">
                          <span className="device-name">{d.name || d.id}</span>
                          <span className="device-serial">{d.serial || 'No serial'}</span>
                        </div>
                        <div className="device-last-seen">Never connected</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {devices.length === 0 && (
                <div className="empty-state">
                  <p>No devices in this portfolio yet.</p>
                </div>
              )}
            </div>
          )}

          {connectionAlertsTab === 'logs' && (
            <div className="logs-panel">
              <h3>Connection Event Log</h3>
              {loadingConnectionEvents ? (
                <div className="loading">Loading events...</div>
              ) : connectionEvents.length === 0 ? (
                <div className="empty-state">
                  <p>No connection events yet.</p>
                  <p className="hint">Events are logged when devices go offline or come back online.</p>
                </div>
              ) : (
                <div className="events-list">
                  {connectionEvents.map(event => (
                    <div key={`${event.deviceId}-${event.id}`} className={`event-item ${event.type}`}>
                      <div className="event-icon">
                        {event.type === 'offline' ? '🔴' : '🟢'}
                      </div>
                      <div className="event-details">
                        <span className="event-device">{event.deviceName}</span>
                        <span className="event-type">
                          {event.type === 'offline' ? 'went offline' : 'came back online'}
                        </span>
                        {event.type === 'online' && event.durationMs > 0 && (
                          <span className="event-duration">
                            (was offline for {Math.round(event.durationMs / 60000)} min)
                          </span>
                        )}
                      </div>
                      <div className="event-time">
                        {event.timestamp?.toDate?.()?.toLocaleString() || 'Unknown'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {connectionAlertsTab === 'contacts' && canEdit && (
            <div className="contacts-panel bulk-alerts-page">
              <h3>Alert Contacts</h3>
              <p className="hint">
                Select portfolio members who should be emailed when devices go offline.
                Connection alerts are sent by email only.
              </p>

              {portfolioUsers.length === 0 ? (
                <p className="hint">
                  No portfolio members yet — invite users from the Team panel before assigning alert contacts.
                </p>
              ) : (
                <div className="bulk-action-bar">
                  <div className="bulk-action-section">
                    <div className="bulk-action-label">
                      Selected users ({connectionAlertUserIds.length} of {portfolioUsers.length})
                    </div>
                    <div className="bulk-chip-row">
                      {portfolioUsers.map(u => {
                        const sel = connectionAlertUserIds.includes(u.uid);
                        return (
                          <button
                            key={u.uid}
                            type="button"
                            className={`bulk-chip ${sel ? 'selected' : ''}`}
                            onClick={() => toggleConnectionAlertUserId(u.uid)}
                            title={u.email}
                          >
                            {u.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {legacyConnectionContacts.length > 0 && (
                <div className="legacy-contacts-warning">
                  <strong>Legacy contacts on this portfolio will be removed when you save:</strong>
                  <ul>
                    {legacyConnectionContacts.map(c => (
                      <li key={c.id}>{c.name || c.email}{c.email ? ` — ${c.email}` : ''}</li>
                    ))}
                  </ul>
                  <p className="hint">
                    If any of these aren't already portfolio members, add them via the Team panel
                    before saving so they keep receiving connection alerts.
                  </p>
                </div>
              )}

              <div className="form-actions" style={{marginTop: '20px'}}>
                <button className="btn-primary" onClick={handleSaveConnectionSettings} disabled={savingConnectionSettings}>
                  {savingConnectionSettings ? 'Saving...' : 'Save Contacts'}
                </button>
              </div>
            </div>
          )}

          {connectionAlertsTab === 'settings' && canEdit && (
            <div className="settings-panel">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={connectionAlertsEnabled}
                    onChange={(e) => setConnectionAlertsEnabled(e.target.checked)}
                  />
                  Enable connection alerts
                </label>
                <p className="hint">Send email notifications when devices go offline</p>
              </div>

              <div className="form-group">
                <label>Offline Threshold (minutes)</label>
                <input
                  type="number"
                  min="2"
                  max="1440"
                  value={offlineThresholdMinutes}
                  onChange={(e) => setOfflineThresholdMinutes(Math.max(2, e.target.value))}
                />
                <p className="hint">Device is considered offline after this many minutes without reporting (minimum 2)</p>
              </div>

              <div className="info-section">
                <div className="info-row">
                  <span className="label">Total Devices:</span>
                  <span className="value">{devices.length}</span>
                </div>
                <div className="info-row">
                  <span className="label">Alert Contacts:</span>
                  <span className="value">{connectionAlertUserIds.length}</span>
                </div>
                <div className="info-row">
                  <span className="label">Alerts Enabled:</span>
                  <span className="value">{connectionAlertsEnabled ? 'Yes' : 'No'}</span>
                </div>
              </div>

              <div className="form-actions">
                <button className="btn-primary" onClick={handleSaveConnectionSettings} disabled={savingConnectionSettings}>
                  {savingConnectionSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}
        </div>
        <style>{`
          /* Bulk-style chip picker for the contacts tab — mirrors the
             "Selected users" section on the Bulk Alert Contacts page. */
          .connection-alerts-page .bulk-action-bar {
            display: flex;
            flex-direction: column;
            gap: 14px;
            background: #1a1a2e;
            border: 1px solid #2a2a4a;
            border-radius: 8px;
            padding: 16px;
            margin: 16px 0 20px;
          }
          .connection-alerts-page .bulk-action-section {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .connection-alerts-page .bulk-action-label {
            color: #888;
            font-size: 13px;
          }
          .connection-alerts-page .bulk-chip-row {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }
          .connection-alerts-page .bulk-chip {
            background: #2a2a4a;
            border: 1px solid #3a3a5a;
            color: #fff;
            padding: 6px 12px;
            border-radius: 14px;
            cursor: pointer;
            font-size: 13px;
          }
          .connection-alerts-page .bulk-chip.selected {
            background: #4ecdc4;
            color: #1a1a2e;
            border-color: #4ecdc4;
          }
          .connection-alerts-page .legacy-contacts-warning {
            margin-top: 16px;
            padding: 14px 16px;
            background: rgba(243, 156, 18, 0.08);
            border: 1px solid rgba(243, 156, 18, 0.3);
            border-radius: 6px;
            color: #f39c12;
            font-size: 13px;
          }
          .connection-alerts-page .legacy-contacts-warning ul { margin: 8px 0; padding-left: 20px; }
          .connection-alerts-page .legacy-contacts-warning li { margin-bottom: 4px; }

          .sensor-page { height: calc(100vh - 60px); background: #16213e; display: flex; flex-direction: column; }
          .sensor-page-header { display: flex; align-items: center; gap: 20px; padding: 20px; background: #1a1a2e; border-bottom: 1px solid #3a3a5a; }
          .sensor-page-header h1 { margin: 0; flex: 1; color: #fff; }
          .back-btn { background: #3a3a5a; border: none; color: #fff; padding: 10px 20px; border-radius: 4px; cursor: pointer; }
          .back-btn:hover { background: #4a4a6a; }
          .sensor-page-tabs { display: flex; gap: 10px; padding: 15px 20px; background: #1a1a2e; border-bottom: 1px solid #3a3a5a; }
          .tab { background: transparent; border: none; color: #888; font-size: 14px; padding: 10px 20px; cursor: pointer; border-radius: 4px; }
          .tab:hover { background: #3a3a5a; color: #fff; }
          .tab.active { background: #4ecdc4; color: #1a1a2e; }
          .sensor-page-content { flex: 1; overflow-y: auto; padding: 20px; }
          .settings-panel, .contacts-panel { max-width: 800px; margin: 0 auto; }
          .form-group { margin-bottom: 20px; }
          .form-group label { display: block; margin-bottom: 8px; font-weight: 600; color: #fff; }
          .form-group input, .form-group select, .form-group textarea { width: 100%; padding: 12px; border: 1px solid #3a3a5a; border-radius: 4px; font-size: 14px; background: #1a1a2e; color: #fff; box-sizing: border-box; }
          .form-group input:focus, .form-group select:focus { outline: none; border-color: #4ecdc4; }
          .hint { color: #888; font-size: 13px; margin-top: 5px; }
          .info-section { background: #1a1a2e; border-radius: 8px; padding: 15px; margin: 20px 0; border: 1px solid #3a3a5a; }
          .info-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #2a2a4a; }
          .info-row:last-child { border-bottom: none; }
          .info-row .label { color: #888; }
          .info-row .value { color: #fff; }
          .form-actions { display: flex; gap: 15px; margin-top: 30px; }
          .btn-primary { background: #4ecdc4; color: #1a1a2e; border: none; padding: 12px 24px; border-radius: 4px; cursor: pointer; font-weight: 600; }
          .btn-primary:hover { background: #3dbdb5; }
          .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
          .btn-secondary { background: #3a3a5a; color: #fff; border: none; padding: 12px 24px; border-radius: 4px; cursor: pointer; }
          .btn-danger { background: #e74c3c; color: #fff; border: none; padding: 12px 24px; border-radius: 4px; cursor: pointer; }
          .checkbox-label { display: flex; align-items: center; gap: 10px; cursor: pointer; color: #fff; }
          .checkbox-label input[type="checkbox"] { width: 18px; height: 18px; cursor: pointer; }
          .connection-status-summary { display: flex; gap: 10px; }
          .status-badge { padding: 6px 14px; border-radius: 20px; font-size: 14px; font-weight: 600; }
          .status-badge.online { background: rgba(46, 204, 113, 0.15); color: #2ecc71; border: 1px solid rgba(46, 204, 113, 0.3); }
          .status-badge.offline { background: rgba(231, 76, 60, 0.15); color: #e74c3c; border: 1px solid rgba(231, 76, 60, 0.3); }
          .status-panel, .logs-panel { max-width: 800px; margin: 0 auto; }
          .status-section { background: #1a1a2e; border-radius: 8px; padding: 20px; margin-bottom: 20px; }
          .status-section h3 { margin: 0 0 15px 0; color: #4ecdc4; font-size: 16px; }
          .offline-section { border: 1px solid rgba(231, 76, 60, 0.3); }
          .offline-section h3 { color: #e74c3c; }
          .online-section { border: 1px solid rgba(46, 204, 113, 0.2); }
          .online-section h3 { color: #2ecc71; }
          .device-status-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
          .device-status-card { display: flex; align-items: center; gap: 12px; padding: 16px; background: #16213e; border-radius: 8px; border: 1px solid #3a3a5a; }
          .device-status-card.online { border-left: 3px solid #2ecc71; }
          .device-status-card.offline { border-left: 3px solid #e74c3c; background: rgba(231, 76, 60, 0.05); }
          .status-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
          .status-dot.online { background: #2ecc71; box-shadow: 0 0 6px rgba(46, 204, 113, 0.6); }
          .status-dot.offline { background: #e74c3c; box-shadow: 0 0 6px rgba(231, 76, 60, 0.6); }
          .status-dot.unknown { background: #95a5a6; }
          .device-status-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
          .device-status-info .device-name { font-weight: 600; color: #fff; display: block; }
          .device-status-info .device-serial { font-size: 12px; color: #4ecdc4; font-family: monospace; display: block; }
          .device-last-seen { font-size: 12px; color: #888; white-space: nowrap; }
          .logs-panel h3 { color: #4ecdc4; margin: 0 0 20px 0; }
          .events-list { background: #1a1a2e; border-radius: 8px; overflow: hidden; }
          .event-item { display: flex; align-items: center; gap: 15px; padding: 14px 16px; border-bottom: 1px solid #2a2a4a; }
          .event-item:last-child { border-bottom: none; }
          .event-icon { font-size: 18px; width: 24px; text-align: center; }
          .event-details { flex: 1; }
          .event-device { font-weight: 600; color: #fff; }
          .event-type { color: #888; margin-left: 6px; }
          .event-duration { color: #666; font-size: 12px; margin-left: 6px; }
          .event-time { font-size: 12px; color: #666; }
          .contacts-panel h3 { color: #4ecdc4; margin: 0 0 10px 0; }
          .add-contact-form { background: #1a1a2e; border-radius: 8px; padding: 20px; margin: 20px 0; }
          .add-contact-form h4 { margin: 0 0 15px 0; color: #fff; }
          .form-row { margin-bottom: 15px; }
          .form-buttons { display: flex; gap: 10px; margin-top: 15px; }
          .contacts-list { margin-top: 20px; }
          .contact-card { display: flex; align-items: center; justify-content: space-between; padding: 12px 15px; background: #1a1a2e; border: 1px solid #3a3a5a; border-radius: 6px; margin-bottom: 8px; }
          .contact-info { display: flex; flex-direction: column; gap: 2px; }
          .contact-name { font-weight: 500; color: #fff; }
          .contact-email { font-size: 13px; color: #4ecdc4; }
          .contact-actions { display: flex; gap: 8px; }
          .btn-small { padding: 6px 12px; font-size: 12px; border: none; border-radius: 4px; cursor: pointer; background: #3a3a5a; color: #fff; }
          .btn-small:hover { background: #4a4a6a; }
          .btn-small.danger { background: rgba(231, 76, 60, 0.3); color: #e74c3c; }
          .btn-small.danger:hover { background: rgba(231, 76, 60, 0.5); }
          .empty-state { text-align: center; padding: 40px; color: #888; }
          .loading { text-align: center; padding: 40px; color: #888; }
        `}</style>
      </div>
    );
  }

  // Sensor Page View
  if (viewingSensor) {
    return (
      <div className="sensor-page">
        <div className="sensor-page-header">
          <button className="back-btn" onClick={closeSensorPage}>← Back</button>
          <h1>{viewingSensor.name}</h1>
          <div className={`sensor-current-temp ${['Offline', 'Device Offline', 'Sensor Error'].includes(getSensorTempDisplay(viewingSensor, devices.find(d => d.id === viewingSensor.device_id), offlineThresholdMinutes)) ? 'offline' : ''}`}>
            {getSensorTempDisplay(viewingSensor, devices.find(d => d.id === viewingSensor.device_id), offlineThresholdMinutes)}
          </div>
        </div>

        <div className="sensor-page-tabs">
          <button
            className={`tab ${activeTab === 'trends' ? 'active' : ''}`}
            onClick={() => setActiveTab('trends')}
          >
            Trends
          </button>
          <button
            className={`tab ${activeTab === 'alarms' ? 'active' : ''}`}
            onClick={() => setActiveTab('alarms')}
          >
            Alarm Log
          </button>
          {canEdit && (
            <button
              className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </button>
          )}
        </div>

        <div className="sensor-page-content">
          {activeTab === 'settings' && canEdit && (
            <div className="settings-panel">
              <div className="form-group">
                <label>Sensor Name</label>
                <input
                  type="text"
                  value={editSensorName}
                  onChange={(e) => setEditSensorName(e.target.value)}
                  placeholder="e.g., Walk-in Cooler"
                />
              </div>
              <div className="form-group">
                <label>Temperature Threshold (°F)</label>
                <input
                  type="number"
                  value={editThreshold}
                  onChange={(e) => setEditThreshold(e.target.value)}
                  min="0"
                  max="200"
                />
                <p className="hint">Alert when temperature exceeds this value</p>
              </div>
              <div className="form-group">
                <label>Sensor Error Delay (minutes)</label>
                <input
                  type="number"
                  value={editErrorDelay}
                  onChange={(e) => setEditErrorDelay(e.target.value)}
                  min="1"
                  max="60"
                />
                <p className="hint">Wait this long before alerting on sensor read errors. Sensor-offline alerts (no readings received) always wait at least 5 minutes, even when this is set lower.</p>
              </div>

              <div className="alert-info-box">
                <h5>How Alerts Work</h5>
                <ul>
                  <li><strong>Temperature Alerts:</strong> One alert when temperature exceeds threshold. Resets when temperature drops 2°F below threshold.</li>
                  <li><strong>Sensor Malfunction:</strong> Alert if sensor reads ~185°F or cannot be read for longer than the error delay.</li>
                  <li><strong>Sensor Offline:</strong> Alert if a sensor stops sending readings for longer than the error delay (at least 5 minutes, even if the delay is set lower). If the whole device drops off, the device-connection alert covers it instead.</li>
                </ul>
              </div>

              <div className="info-section">
                <div className="info-row">
                  <span className="label">Sensor ID:</span>
                  <span className="value mono">{viewingSensor.sensor_id}</span>
                </div>
                <div className="info-row">
                  <span className="label">Current Temp:</span>
                  <span className={`value ${['Offline', 'Device Offline', 'Sensor Error'].includes(getSensorTempDisplay(viewingSensor, devices.find(d => d.id === viewingSensor.device_id), offlineThresholdMinutes)) ? 'offline' : ''}`}>{getSensorTempDisplay(viewingSensor, devices.find(d => d.id === viewingSensor.device_id), offlineThresholdMinutes)}</span>
                </div>
              </div>

              <h3 className="section-title">Alert Contacts</h3>
              <p className="hint">
                Select portfolio members who should receive alerts for this sensor.
                Email goes to anyone selected; SMS only sends to members who have
                a phone number and SMS carrier on file in their account settings.
              </p>

              <UserAlertPicker
                users={portfolioUsers}
                selectedIds={editContactUserIds}
                onToggle={toggleContactUserId}
                search={contactPickerSearch}
                onSearchChange={setContactPickerSearch}
                channelMode="email-and-sms"
                emptyHint="No portfolio members yet — invite users from the Team panel before assigning alert contacts."
              />

              {legacySensorContacts.length > 0 && (
                <div className="legacy-contacts-warning">
                  <strong>Legacy contacts on this sensor will be removed when you save:</strong>
                  <ul>
                    {legacySensorContacts.map(c => (
                      <li key={c.id}>
                        {c.name || c.email || c.phone}
                        {c.email ? ` — ${c.email}` : ''}
                        {c.phone ? ` — ${c.phone}${c.carrier ? '@' + c.carrier : ''}` : ''}
                      </li>
                    ))}
                  </ul>
                  <p className="hint">
                    If any of these aren't already portfolio members, add them via the Team panel
                    before saving so they keep receiving alerts.
                  </p>
                </div>
              )}

              <div className="form-actions">
                <button className="btn-primary" onClick={handleSaveSensorSettings}>Save Settings</button>
                <button className="btn-danger" onClick={handleDeleteSensor}>Delete Sensor</button>
              </div>
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="trends-panel">
              <div className="current-reading">
                <div className={`big-temp ${['Offline', 'Device Offline', 'Sensor Error'].includes(getSensorTempDisplay(viewingSensor, devices.find(d => d.id === viewingSensor.device_id), offlineThresholdMinutes)) ? 'offline' : ''}`}>{getSensorTempDisplay(viewingSensor, devices.find(d => d.id === viewingSensor.device_id), offlineThresholdMinutes)}</div>
                <div className="reading-time">
                  {viewingSensor.last_reading_at
                    ? `Last reading: ${new Date(viewingSensor.last_reading_at.toDate()).toLocaleString()}`
                    : 'No readings yet'}
                </div>
                <div className="threshold-line">Threshold: {viewingSensor.threshold}°F</div>
              </div>

              {loadingReadings ? (
                <div className="loading-readings">Loading history...</div>
              ) : readings.length === 0 ? (
                <div className="no-readings">
                  <p>No historical data yet</p>
                  <p className="hint">Temperature readings will appear here as they are collected (every 15 minutes).</p>
                </div>
              ) : (
                <>
                  <div className="time-range-selector">
                    <button
                      className={trendTimeRange === '12h' ? 'active' : ''}
                      onClick={() => setTrendTimeRange('12h')}
                    >12 hours</button>
                    <button
                      className={trendTimeRange === '24h' ? 'active' : ''}
                      onClick={() => setTrendTimeRange('24h')}
                    >24 hours</button>
                    <button
                      className={trendTimeRange === '7d' ? 'active' : ''}
                      onClick={() => setTrendTimeRange('7d')}
                    >7 days</button>
                  </div>
                  <div className="readings-chart">
                    {(() => {
                      // Data is already filtered by the Firestore query based on trendTimeRange
                      const chartData = readings.slice().reverse().filter(r => r.timestamp);

                      if (chartData.length === 0) {
                        return <div className="no-readings"><p>No data in this time range</p></div>;
                      }

                      const minTemp = Math.min(...chartData.map(x => x.temp));
                      const maxTemp = Math.max(...chartData.map(x => x.temp));
                      const range = maxTemp - minTemp || 1;
                      const padding = range * 0.15;
                      const yMin = minTemp - padding;
                      const yMax = maxTemp + padding;
                      const yRange = yMax - yMin;
                      const width = 700;
                      const height = 260;
                      const marginTop = 18;
                      const marginLeft = 60;
                      const marginBottom = 40;
                      const chartWidth = width - marginLeft;
                      const chartHeight = height - marginTop - marginBottom;
                      const threshold = viewingSensor.threshold;

                      // Time-scaled X axis: positions proportional to real elapsed time
                      const timeMin = chartData[0].timestamp.getTime();
                      const timeMax = chartData[chartData.length - 1].timestamp.getTime();
                      const timeSpan = timeMax - timeMin || 1;
                      const timeToX = (ts) => marginLeft + ((ts - timeMin) / timeSpan) * chartWidth;
                      // Y helper that accounts for top margin
                      const tempToY = (temp) => marginTop + chartHeight - ((temp - yMin) / yRange) * chartHeight;

                      // Gap threshold: 30 min for 12h/24h, 2 hours for 7d
                      const GAP_THRESHOLD_MS = trendTimeRange === '7d' ? 2 * 60 * 60 * 1000 : 30 * 60 * 1000;

                      // Calculate nice Y axis ticks (only 3 ticks to avoid crowding)
                      const yTicks = [];
                      const niceMin = Math.floor(yMin);
                      const niceMax = Math.ceil(yMax);
                      const yStep = Math.ceil((niceMax - niceMin) / 2) || 1;
                      for (let t = niceMin; t <= niceMax; t += yStep) {
                        yTicks.push(t);
                      }

                      // Calculate X axis labels based on time range
                      const xLabels = [];
                      if (trendTimeRange === '7d') {
                        // Show day boundaries (midnight of each day)
                        const startDate = new Date(timeMin);
                        const firstMidnight = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1);
                        for (let d = firstMidnight.getTime(); d <= timeMax; d += 24 * 60 * 60 * 1000) {
                          const date = new Date(d);
                          xLabels.push({
                            x: timeToX(d),
                            label: date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }),
                            isMidnight: true
                          });
                        }
                      } else {
                        // For 12h/24h show ~5 evenly spaced time labels, rounded to nearest 15 min
                        const FIFTEEN_MIN = 15 * 60 * 1000;
                        const labelCount = 5;
                        for (let i = 0; i < labelCount; i++) {
                          const t = timeMin + (i / (labelCount - 1)) * timeSpan;
                          const rounded = Math.round(t / FIFTEEN_MIN) * FIFTEEN_MIN;
                          const date = new Date(rounded);
                          xLabels.push({
                            x: timeToX(rounded),
                            label: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                            isMidnight: false
                          });
                        }
                        // Add midnight day-split lines if data spans across days
                        const startDate = new Date(timeMin);
                        const firstMidnight = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1);
                        for (let d = firstMidnight.getTime(); d <= timeMax; d += 24 * 60 * 60 * 1000) {
                          const date = new Date(d);
                          xLabels.push({
                            x: timeToX(d),
                            label: date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }),
                            isMidnight: true
                          });
                        }
                      }

                      // Build line segments, breaking at gaps
                      const segments = [];
                      let currentSegment = [];

                      chartData.forEach((r, i) => {
                        const x = timeToX(r.timestamp.getTime());
                        const y = tempToY(r.temp);

                        if (i > 0) {
                          const prevTime = chartData[i - 1].timestamp.getTime();
                          const currTime = r.timestamp.getTime();
                          if (currTime - prevTime > GAP_THRESHOLD_MS) {
                            if (currentSegment.length > 0) segments.push(currentSegment);
                            currentSegment = [];
                          }
                        }

                        currentSegment.push(`${x},${y}`);
                      });
                      if (currentSegment.length > 0) segments.push(currentSegment);

                      const thresholdY = tempToY(threshold);
                      const xAxisY = marginTop + chartHeight;

                      return (
                        <svg viewBox={`0 0 ${width} ${height}`} className="line-chart">
                          {/* Grid lines - horizontal */}
                          {yTicks.map((tick, i) => {
                            const y = tempToY(tick);
                            // Skip Y label if it's too close to the X axis (within 14px)
                            const tooCloseToXAxis = y > xAxisY - 14;
                            return (
                              <g key={`y-${i}`}>
                                <line
                                  x1={marginLeft} y1={y} x2={width} y2={y}
                                  stroke="#4a4a6a" strokeWidth="1"
                                />
                                <line
                                  x1={marginLeft - 5} y1={y} x2={marginLeft} y2={y}
                                  stroke="#aaa" strokeWidth="2"
                                />
                                {!tooCloseToXAxis && (
                                  <text x={marginLeft - 10} y={y + 5} fill="#aaa" fontSize="13" textAnchor="end">
                                    {tick}°F
                                  </text>
                                )}
                              </g>
                            );
                          })}

                          {/* Grid lines - vertical + X axis labels */}
                          {xLabels.filter(label => label.x > marginLeft + 30).map((label, i) => (
                            <g key={`x-${i}`}>
                              <line
                                x1={label.x} y1={marginTop} x2={label.x} y2={xAxisY}
                                stroke={label.isMidnight ? '#666' : '#4a4a6a'} strokeWidth="1"
                                strokeDasharray={label.isMidnight ? '4,2' : 'none'}
                              />
                              <line
                                x1={label.x} y1={xAxisY} x2={label.x} y2={xAxisY + 5}
                                stroke="#aaa" strokeWidth="2"
                              />
                              <text
                                x={label.x} y={xAxisY + 22} fill="#aaa" fontSize="11" textAnchor="middle"
                              >
                                {label.label}
                              </text>
                            </g>
                          ))}

                          {/* Threshold line */}
                          <line
                            x1={marginLeft} y1={thresholdY} x2={width} y2={thresholdY}
                            stroke="#e74c3c" strokeWidth="2" strokeDasharray="5,5" opacity="0.8"
                          />
                          <text x={width - 5} y={thresholdY - 5} fill="#e74c3c" fontSize="12" textAnchor="end">
                            {threshold}°F
                          </text>

                          {/* Data line segments (breaks shown for gaps) */}
                          {segments.map((segment, i) => (
                            <polyline
                              key={`segment-${i}`}
                              fill="none"
                              stroke="#4ecdc4"
                              strokeWidth="2"
                              points={segment.join(' ')}
                            />
                          ))}

                          {/* Data points (skip dots for 7d to avoid clutter) */}
                          {trendTimeRange !== '7d' && chartData.map((r, i) => {
                            const x = timeToX(r.timestamp.getTime());
                            const y = tempToY(r.temp);
                            const isOverThreshold = r.temp > threshold;
                            return (
                              <circle
                                key={r.id}
                                cx={x}
                                cy={y}
                                r="5"
                                fill={isOverThreshold ? '#e74c3c' : '#4ecdc4'}
                                stroke={hoveredPoint?.id === r.id ? '#fff' : 'none'}
                                strokeWidth="2"
                                style={{ cursor: 'pointer' }}
                                onMouseEnter={() => setHoveredPoint({ id: r.id, x, y, temp: r.temp, time: r.timestamp })}
                                onMouseLeave={() => setHoveredPoint(null)}
                              />
                            );
                          })}

                          {/* Tooltip */}
                          {hoveredPoint && (
                            <g>
                              <rect
                                x={hoveredPoint.x - 70}
                                y={hoveredPoint.y - 45}
                                width="140"
                                height="38"
                                rx="4"
                                fill="rgba(0,0,0,0.85)"
                                stroke="#4ecdc4"
                                strokeWidth="1"
                              />
                              <text x={hoveredPoint.x} y={hoveredPoint.y - 28} fill="#fff" fontSize="13" textAnchor="middle" fontWeight="bold">
                                {hoveredPoint.temp.toFixed(1)}°F
                              </text>
                              <text x={hoveredPoint.x} y={hoveredPoint.y - 12} fill="#aaa" fontSize="11" textAnchor="middle">
                                {hoveredPoint.time?.toLocaleString() || 'Unknown'}
                              </text>
                            </g>
                          )}

                          {/* Y axis line */}
                          <line x1={marginLeft} y1={marginTop} x2={marginLeft} y2={xAxisY} stroke="#888" strokeWidth="2" />
                          {/* X axis line */}
                          <line x1={marginLeft} y1={xAxisY} x2={width} y2={xAxisY} stroke="#888" strokeWidth="2" />
                        </svg>
                      );
                    })()}
                  </div>

                  <div className="readings-list">
                    <h4>Recent Readings</h4>
                    <div className="readings-table">
                      {readings.slice(0, 20).map((r) => (
                        <div key={r.id} className={`reading-row ${r.temp > viewingSensor.threshold ? 'over-threshold' : ''}`}>
                          <span className="reading-temp">{r.temp.toFixed(1)}°F</span>
                          <span className="reading-time">{r.timestamp?.toLocaleString() || 'Unknown'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'alarms' && (
            <div className="alarms-panel">
              <h3>Alarm History</h3>
              <p className="hint">Log of temperature alerts that were triggered.</p>

              {loadingAlarms ? (
                <div className="loading-alarms">Loading alarm history...</div>
              ) : alarmLogs.length === 0 ? (
                <div className="no-alarms">
                  <p>No alarms recorded yet</p>
                  <p className="hint">Alarms will appear here when temperature exceeds the threshold.</p>
                </div>
              ) : (
                <div className="alarms-list">
                  {alarmLogs.map(alarm => (
                    <div key={alarm.id} className="alarm-card">
                      <div className="alarm-header">
                        <span className="alarm-temp">{alarm.temp?.toFixed(1)}°F</span>
                        <span className="alarm-threshold">Threshold: {alarm.threshold}°F</span>
                      </div>
                      <div className="alarm-time">
                        {alarm.timestamp?.toLocaleString() || 'Unknown time'}
                      </div>
                      {alarm.message && (
                        <div className="alarm-message">{alarm.message}</div>
                      )}
                      {alarm.notified && alarm.notified.length > 0 && (
                        <div className="alarm-notified">
                          <span className="notified-label">Notified:</span>
                          {alarm.notified.map((n, i) => (
                            <span key={i} className="notified-contact">{n}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <style>{`
          .sensor-page {
            height: calc(100vh - 60px);
            background: #16213e;
            display: flex;
            flex-direction: column;
          }
          .sensor-page-header {
            display: flex;
            align-items: center;
            gap: 20px;
            padding: 20px;
            background: #1a1a2e;
            border-bottom: 1px solid #3a3a5a;
          }
          .sensor-page-header h1 {
            margin: 0;
            flex: 1;
            color: #fff;
          }
          .sensor-current-temp {
            font-size: 32px;
            font-weight: bold;
            color: #4ecdc4;
          }
          .sensor-current-temp.offline {
            color: #888;
          }
          .sensor-temp.offline {
            color: #888 !important;
          }
          .big-temp.offline {
            color: #888 !important;
          }
          .value.offline {
            color: #888 !important;
          }
          .sensor-card-inline.offline {
            opacity: 0.7;
            border-left: 3px solid #888;
          }
          .back-btn {
            background: #3a3a5a;
            border: none;
            color: #fff;
            padding: 10px 20px;
            border-radius: 4px;
            cursor: pointer;
          }
          .back-btn:hover {
            background: #4a4a6a;
          }
          .sensor-page-tabs {
            display: flex;
            gap: 10px;
            padding: 15px 20px;
            background: #1a1a2e;
            border-bottom: 1px solid #3a3a5a;
          }
          .tab {
            background: transparent;
            border: none;
            color: #888;
            font-size: 14px;
            padding: 10px 20px;
            cursor: pointer;
            border-radius: 4px;
          }
          .tab:hover {
            background: #3a3a5a;
            color: #fff;
          }
          .tab.active {
            background: #4ecdc4;
            color: #1a1a2e;
          }
          .sensor-page-content {
            flex: 1;
            overflow-y: auto;
            padding: 20px;
          }
          .settings-panel, .contacts-panel, .trends-panel {
            max-width: 800px;
            margin: 0 auto;
          }
          .form-group {
            margin-bottom: 20px;
          }
          .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 600;
            color: #fff;
          }
          .form-group input, .form-group select {
            width: 100%;
            padding: 12px;
            border: 1px solid #3a3a5a;
            border-radius: 4px;
            font-size: 14px;
            background: #1a1a2e;
            color: #fff;
          }
          .form-group input:focus, .form-group select:focus {
            outline: none;
            border-color: #4ecdc4;
          }
          .hint {
            font-size: 13px;
            color: #888;
            margin-top: 5px;
          }
          .info-section {
            background: #1a1a2e;
            border-radius: 8px;
            padding: 15px;
            margin: 20px 0;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 10px 0;
            border-bottom: 1px solid #2a2a4a;
          }
          .info-row:last-child {
            border-bottom: none;
          }
          .info-row .label {
            color: #888;
          }
          .info-row .value {
            color: #fff;
          }
          .info-row .mono {
            font-family: monospace;
            color: #4ecdc4;
          }
          .form-actions {
            display: flex;
            gap: 15px;
            margin-top: 30px;
          }
          .btn-primary {
            background: #4ecdc4;
            color: #1a1a2e;
            border: none;
            padding: 12px 24px;
            border-radius: 4px;
            cursor: pointer;
            font-weight: 600;
          }
          .btn-primary:hover {
            background: #3dbdb5;
          }
          .btn-danger {
            background: #e74c3c;
            color: #fff;
            border: none;
            padding: 12px 24px;
            border-radius: 4px;
            cursor: pointer;
            margin-left: auto;
          }
          .btn-danger:hover {
            background: #c0392b;
          }
          .contacts-panel h3 {
            margin: 0 0 10px 0;
            color: #4ecdc4;
          }
          .add-contact-form {
            background: #1a1a2e;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
          }
          .add-contact-form h4 {
            margin: 0 0 15px 0;
            color: #fff;
          }
          .form-row {
            margin-bottom: 15px;
          }
          .form-row.two-col {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
          }
          .contacts-list {
            margin-top: 30px;
          }
          .contacts-list h4 {
            margin: 0 0 15px 0;
            color: #4ecdc4;
          }
          .no-contacts {
            color: #888;
            padding: 20px;
            text-align: center;
            background: #1a1a2e;
            border-radius: 8px;
          }
          .contacts-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 15px;
          }
          .contact-card {
            background: #1a1a2e;
            border-radius: 8px;
            padding: 16px;
            border: 1px solid #3a3a5a;
            transition: border-color 0.2s;
          }
          .contact-card:hover {
            border-color: #4a4a6a;
          }
          .contact-card.editing {
            border-color: #4ecdc4;
          }
          .contact-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 12px;
          }
          .contact-card .contact-name {
            font-weight: 600;
            color: #fff;
            font-size: 16px;
          }
          .contact-card .contact-actions {
            display: flex;
            gap: 6px;
          }
          .btn-icon {
            background: #3a3a5a;
            border: none;
            color: #888;
            width: 28px;
            height: 28px;
            border-radius: 4px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background 0.2s, color 0.2s;
          }
          .btn-icon:hover {
            background: #4ecdc4;
            color: #1a1a2e;
          }
          .btn-icon.danger:hover {
            background: #e74c3c;
            color: #fff;
          }
          .contact-info {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .contact-row {
            display: flex;
            align-items: baseline;
            gap: 10px;
          }
          .contact-label {
            font-size: 11px;
            color: #666;
            text-transform: uppercase;
            font-weight: 600;
            min-width: 40px;
          }
          .contact-value {
            font-size: 13px;
            color: #ccc;
            word-break: break-all;
          }
          .contact-value.empty {
            color: #666;
            font-style: italic;
          }
          .carrier-tag {
            background: #3a3a5a;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 11px;
            color: #4ecdc4;
            margin-left: 6px;
          }
          .user-alert-picker { margin-top: 16px; }
          .picker-toolbar {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 12px;
          }
          .picker-search {
            flex: 1;
            padding: 10px 12px;
            border: 1px solid #3a3a5a;
            border-radius: 4px;
            background: #16213e;
            color: #fff;
            font-size: 14px;
          }
          .picker-search:focus { outline: none; border-color: #4ecdc4; }
          .picker-count { color: #888; font-size: 13px; white-space: nowrap; }
          .picker-rows {
            background: #1a1a2e;
            border-radius: 8px;
            border: 1px solid #2a2a4a;
            overflow: hidden;
          }
          .picker-row {
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 12px 16px;
            border-bottom: 1px solid #2a2a4a;
            cursor: pointer;
            transition: background 0.1s;
          }
          .picker-row:last-child { border-bottom: none; }
          .picker-row:hover { background: #20284a; }
          .picker-row.selected { background: rgba(78, 205, 196, 0.1); }
          .picker-row input[type="checkbox"] {
            width: 18px;
            height: 18px;
            cursor: pointer;
            accent-color: #4ecdc4;
          }
          .picker-user { flex: 1; min-width: 0; }
          .picker-name { color: #fff; font-weight: 600; }
          .picker-email { color: #888; font-size: 12px; }
          .picker-sms { font-size: 12px; white-space: nowrap; }
          .picker-sms.ready { color: #4ecdc4; }
          .picker-sms.missing { color: #f39c12; }
          .picker-stale-warning {
            margin-top: 12px;
            padding: 10px 12px;
            background: rgba(231, 76, 60, 0.1);
            border: 1px solid rgba(231, 76, 60, 0.3);
            border-radius: 4px;
            color: #f39c12;
            font-size: 13px;
          }
          .legacy-contacts-warning {
            margin-top: 16px;
            padding: 14px 16px;
            background: rgba(243, 156, 18, 0.08);
            border: 1px solid rgba(243, 156, 18, 0.3);
            border-radius: 6px;
            color: #f39c12;
            font-size: 13px;
          }
          .legacy-contacts-warning ul { margin: 8px 0; padding-left: 20px; }
          .legacy-contacts-warning li { margin-bottom: 4px; }
          .trends-panel {
            text-align: center;
          }
          .trends-panel .time-range-selector {
            display: flex;
            justify-content: center;
            gap: 8px;
            margin-bottom: 20px;
          }
          .trends-panel .time-range-selector button {
            background: #2a2a4a;
            border: 1px solid #444;
            color: #888;
            padding: 8px 16px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            transition: all 0.2s;
          }
          .trends-panel .time-range-selector button:hover {
            background: #3a3a5a;
            color: #fff;
            border-color: #555;
          }
          .trends-panel .time-range-selector button.active {
            background: #4ecdc4;
            color: #1a1a2e;
            border-color: #4ecdc4;
          }
          .current-reading {
            margin-bottom: 30px;
          }
          .big-temp {
            font-size: 64px;
            font-weight: bold;
            color: #4ecdc4;
          }
          .reading-time {
            font-size: 14px;
            color: #888;
            margin-top: 10px;
          }
          .threshold-line {
            font-size: 16px;
            color: #888;
            margin-top: 10px;
          }
          .loading-readings {
            padding: 40px;
            color: #888;
          }
          .no-readings {
            background: #1a1a2e;
            border-radius: 8px;
            padding: 40px 20px;
            color: #888;
          }
          .readings-stats {
            display: flex;
            justify-content: center;
            gap: 40px;
            margin-bottom: 30px;
            padding: 20px;
            background: #1a1a2e;
            border-radius: 8px;
          }
          .stat {
            text-align: center;
          }
          .stat-label {
            display: block;
            font-size: 12px;
            color: #888;
            margin-bottom: 5px;
          }
          .stat-value {
            display: block;
            font-size: 24px;
            font-weight: bold;
            color: #fff;
          }
          .readings-chart {
            background: #1a1a2e;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 30px;
          }
          .line-chart {
            width: 100%;
            height: 200px;
          }
          .readings-list {
            text-align: left;
          }
          .readings-list h4 {
            margin: 0 0 15px 0;
            color: #4ecdc4;
          }
          .readings-table {
            background: #1a1a2e;
            border-radius: 8px;
            max-height: 300px;
            overflow-y: auto;
          }
          .reading-row {
            display: flex;
            justify-content: space-between;
            padding: 12px 15px;
            border-bottom: 1px solid #2a2a4a;
          }
          .reading-row:last-child {
            border-bottom: none;
          }
          .reading-row.over-threshold {
            background: rgba(231, 76, 60, 0.15);
          }
          .reading-row.over-threshold .reading-temp {
            color: #e74c3c;
          }
          .reading-temp {
            font-weight: 600;
            color: #4ecdc4;
          }
          .reading-time {
            font-size: 13px;
            color: #888;
          }
          .form-buttons {
            display: flex;
            gap: 10px;
          }
          .btn-secondary {
            background: #3a3a5a;
            color: #fff;
            border: none;
            padding: 12px 24px;
            border-radius: 4px;
            cursor: pointer;
          }
          .btn-secondary:hover {
            background: #4a4a6a;
          }
          .contact-actions {
            display: flex;
            gap: 8px;
          }
          .btn-edit {
            background: #3a3a5a;
            color: #fff;
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
          }
          .btn-edit:hover {
            background: #4ecdc4;
            color: #1a1a2e;
          }
          .contact-card.editing {
            border: 2px solid #4ecdc4;
          }
          .alarms-panel {
            max-width: 800px;
            margin: 0 auto;
          }
          .alarms-panel h3 {
            margin: 0 0 10px 0;
            color: #4ecdc4;
          }
          .loading-alarms, .no-alarms {
            background: #1a1a2e;
            border-radius: 8px;
            padding: 40px 20px;
            text-align: center;
            color: #888;
          }
          .alarms-list {
            margin-top: 20px;
          }
          .alarm-card {
            background: #1a1a2e;
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 10px;
            border-left: 4px solid #e74c3c;
          }
          .alarm-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
          }
          .alarm-temp {
            font-size: 20px;
            font-weight: bold;
            color: #e74c3c;
          }
          .alarm-threshold {
            color: #888;
            font-size: 13px;
          }
          .alarm-time {
            color: #888;
            font-size: 13px;
            margin-bottom: 8px;
          }
          .alarm-message {
            color: #fff;
            font-size: 14px;
            margin-bottom: 8px;
            padding: 8px;
            background: rgba(231, 76, 60, 0.1);
            border-radius: 4px;
          }
          .alarm-notified {
            font-size: 12px;
            color: #888;
          }
          .notified-label {
            margin-right: 8px;
          }
          .notified-contact {
            background: #2a2a4a;
            padding: 2px 8px;
            border-radius: 4px;
            margin-right: 5px;
          }
        `}</style>
      </div>
    );
  }

  // Compact all-sensors table — the alternate portfolio view for scanning
  // temps and quick data. Rows open the same sensor page as the cards.
  const renderSensorTable = () => {
    const relTime = (ts) => {
      if (!ts) return 'never';
      const t = ts.toDate ? ts.toDate().getTime() : new Date(ts).getTime();
      const mins = Math.floor((Date.now() - t) / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
      return `${Math.floor(mins / 1440)}d ago`;
    };

    let rows = sensors.map((s) => {
      const device = devices.find((d) => d.id === s.device_id);
      const building = buildings.find((b) => b.id === device?.building_id);
      const tempDisplay = getSensorTempDisplay(s, device, offlineThresholdMinutes);
      const isOffline = ['Offline', 'Device Offline', 'Sensor Error'].includes(tempDisplay);
      const isAlert = !isOffline && s.current_temp && s.current_temp > (s.threshold || 80);
      return { s, device, building, tempDisplay, isOffline, isAlert };
    });
    if (selectedBuilding) {
      rows = rows.filter((r) => r.device?.building_id === selectedBuilding.id);
    }

    const val = (r, key) => {
      switch (key) {
        case 'status': return r.isOffline ? 0 : (r.isAlert ? 1 : 2);
        case 'sensor': return (r.s.name || '').toLowerCase();
        case 'temp': return (r.isOffline || !r.s.current_temp) ? -999 : r.s.current_temp;
        case 'threshold': return r.s.threshold || 45;
        case 'building': return (r.building?.name || '￿').toLowerCase();
        case 'device': return (r.device?.name || '').toLowerCase();
        case 'wifi': return r.device?.wifi_signal?.percent ?? -1;
        case 'reading': {
          const ts = r.s.last_reading_at;
          return ts ? (ts.toDate ? ts.toDate().getTime() : new Date(ts).getTime()) : 0;
        }
        default: return 0;
      }
    };
    const { key: sortKey, dir: sortDir } = portfolioTableSort;
    rows.sort((a, b) => {
      const va = val(a, sortKey);
      const vb = val(b, sortKey);
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return cmp * sortDir;
    });

    const SortTh = ({ k, children }) => (
      <th
        className="sortable"
        onClick={() => setPortfolioTableSort((s) => ({ key: k, dir: s.key === k ? -s.dir : 1 }))}
      >
        {children}
        <span className="sort-arrow">{sortKey === k ? (sortDir === 1 ? ' ▲' : ' ▼') : ''}</span>
      </th>
    );

    return (
      <div className="section sensor-table-wrap">
        <table className="sensor-table">
          <thead>
            <tr>
              <SortTh k="status"></SortTh>
              <SortTh k="sensor">Sensor</SortTh>
              <SortTh k="temp">Temp</SortTh>
              <SortTh k="threshold">Threshold</SortTh>
              <SortTh k="building">Building</SortTh>
              <SortTh k="device">Device</SortTh>
              <SortTh k="wifi">WiFi</SortTh>
              <SortTh k="reading">Last Reading</SortTh>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ s, device, building, tempDisplay, isOffline, isAlert }) => (
              <tr key={s.id} className="sensor-row" onClick={() => openSensorPage(s)}>
                <td><span className={`status-dot ${isOffline ? 'offline' : (isAlert ? 'alert' : 'online')}`}></span></td>
                <td className="cell-sensor">{s.name}</td>
                <td className={`cell-temp ${isOffline ? 'offline' : ''} ${isAlert ? 'alert' : ''}`}>{tempDisplay}</td>
                <td>{s.threshold || 45}&deg;F</td>
                <td>{building?.name || '—'}</td>
                <td>{device?.name || '—'}</td>
                <td>{device?.wifi_signal?.percent != null ? `${device.wifi_signal.percent}%` : '—'}</td>
                <td>{relTime(s.last_reading_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="hint">No sensors{selectedBuilding ? ' in this building' : ''} yet.</p>
        )}
      </div>
    );
  };

  // All-portfolios home view: everything the user can see, as summary
  // cards (one per portfolio) or one big sortable sensor table.
  const renderAllPortfoliosOverview = () => {
    const relTime = (ts) => {
      if (!ts) return 'never';
      const t = ts.toDate ? ts.toDate().getTime() : new Date(ts).getTime();
      const mins = Math.floor((Date.now() - t) / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
      return `${Math.floor(mins / 1440)}d ago`;
    };
    const thresholdFor = (portfolioId) => {
      const p = portfolios.find((x) => x.id === portfolioId);
      return Math.max(2, p?.offlineThresholdMinutes || 20);
    };

    const rows = overview.sensors.map((s) => {
      const device = overview.devices.find((d) => d.id === s.device_id && d.portfolioId === s.portfolioId);
      const building = overview.buildings.find((b) => b.id === device?.building_id && b.portfolioId === s.portfolioId);
      const tempDisplay = getSensorTempDisplay(s, device, thresholdFor(s.portfolioId));
      const isOffline = ['Offline', 'Device Offline', 'Sensor Error'].includes(tempDisplay);
      const isAlert = !isOffline && s.current_temp && s.current_temp > (s.threshold || 80);
      return { s, device, building, tempDisplay, isOffline, isAlert };
    });

    const openFromOverview = (r) => {
      const p = portfolios.find((x) => x.id === r.s.portfolioId);
      if (!p) return;
      setSelectedPortfolio(p);
      setSelectedBuilding(null);
      openSensorPage(r.s);
    };

    const val = (r, key) => {
      switch (key) {
        case 'status': return r.isOffline ? 0 : (r.isAlert ? 1 : 2);
        case 'sensor': return (r.s.name || '').toLowerCase();
        case 'temp': return (r.isOffline || !r.s.current_temp) ? -999 : r.s.current_temp;
        case 'threshold': return r.s.threshold || 45;
        case 'portfolio': return (r.s.portfolioName || '').toLowerCase();
        case 'building': return (r.building?.name || '￿').toLowerCase();
        case 'device': return (r.device?.name || '').toLowerCase();
        case 'reading': {
          const ts = r.s.last_reading_at;
          return ts ? (ts.toDate ? ts.toDate().getTime() : new Date(ts).getTime()) : 0;
        }
        default: return 0;
      }
    };
    const { key: sortKey, dir: sortDir } = portfolioTableSort;
    const sortedRows = [...rows].sort((a, b) => {
      const va = val(a, sortKey);
      const vb = val(b, sortKey);
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return cmp * sortDir;
    });

    const SortTh = ({ k, children }) => (
      <th
        className="sortable"
        onClick={() => setPortfolioTableSort((s) => ({ key: k, dir: s.key === k ? -s.dir : 1 }))}
      >
        {children}
        <span className="sort-arrow">{sortKey === k ? (sortDir === 1 ? ' ▲' : ' ▼') : ''}</span>
      </th>
    );

    const perPortfolio = portfolios.map((p) => {
      const prows = rows.filter((r) => r.s.portfolioId === p.id);
      return {
        p,
        total: prows.length,
        alerts: prows.filter((r) => r.isAlert).length,
        offline: prows.filter((r) => r.isOffline).length
      };
    });

    return (
      <div className="portfolio-overview">
        <div className="portfolio-header">
          <h2>All Portfolios</h2>
          <div className="view-toggle">
            <button
              className={portfolioView === 'cards' ? 'active' : ''}
              onClick={() => switchPortfolioView('cards')}
            >
              Cards
            </button>
            <button
              className={portfolioView === 'table' ? 'active' : ''}
              onClick={() => switchPortfolioView('table')}
            >
              Table
            </button>
          </div>
        </div>

        {overviewLoading && rows.length === 0 ? (
          <div className="empty-state"><p>Loading all portfolios...</p></div>
        ) : portfolioView === 'cards' ? (
          <div className="overview-cards">
            {perPortfolio.map(({ p, total, alerts, offline }) => (
              <div
                key={p.id}
                className={`overview-card ${offline > 0 ? 'has-offline' : ''} ${alerts > 0 ? 'has-alert' : ''}`}
                onClick={() => { setSelectedPortfolio(p); setSelectedBuilding(null); setShowTeamPanel(false); }}
              >
                <h3>{p.name}</h3>
                <div className="overview-stats">
                  <span className="stat">{total} sensor{total === 1 ? '' : 's'}</span>
                  {alerts > 0 && <span className="stat alert">{alerts} alert{alerts === 1 ? '' : 's'}</span>}
                  {offline > 0 && <span className="stat offline">{offline} offline</span>}
                  {alerts === 0 && offline === 0 && total > 0 && <span className="stat ok">All normal</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="section sensor-table-wrap">
            <table className="sensor-table">
              <thead>
                <tr>
                  <SortTh k="status"></SortTh>
                  <SortTh k="sensor">Sensor</SortTh>
                  <SortTh k="temp">Temp</SortTh>
                  <SortTh k="threshold">Threshold</SortTh>
                  <SortTh k="portfolio">Portfolio</SortTh>
                  <SortTh k="building">Building</SortTh>
                  <SortTh k="device">Device</SortTh>
                  <SortTh k="reading">Last Reading</SortTh>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => (
                  <tr key={`${r.s.portfolioId}/${r.s.device_id}/${r.s.id}`} className="sensor-row" onClick={() => openFromOverview(r)}>
                    <td><span className={`status-dot ${r.isOffline ? 'offline' : (r.isAlert ? 'alert' : 'online')}`}></span></td>
                    <td className="cell-sensor">{r.s.name}</td>
                    <td className={`cell-temp ${r.isOffline ? 'offline' : ''} ${r.isAlert ? 'alert' : ''}`}>{r.tempDisplay}</td>
                    <td>{r.s.threshold || 45}&deg;F</td>
                    <td>{r.s.portfolioName}</td>
                    <td>{r.building?.name || '—'}</td>
                    <td>{r.device?.name || '—'}</td>
                    <td>{relTime(r.s.last_reading_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sortedRows.length === 0 && <p className="hint">No sensors configured yet.</p>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="portfolios-layout">
      {/* Sidebar overlay for mobile */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <button className="sidebar-close-btn" onClick={() => setSidebarOpen(false)}>&#10005;</button>
          <h3>Portfolios</h3>
          {isSystemAdmin() && (
            <button className="add-portfolio-btn" onClick={() => setShowNewPortfolio(true)}>+</button>
          )}
        </div>

        <div className="sidebar-content">
          {portfolios.length > 0 && (
            <div
              className={`sidebar-item all-portfolios ${!selectedPortfolio ? 'active' : ''}`}
              onClick={() => {
                setSelectedPortfolio(null);
                setSelectedBuilding(null);
                setShowTeamPanel(false);
                setSidebarOpen(false);
              }}
            >
              <span className="tree-icon">⌂</span>
              All Portfolios
            </div>
          )}
          {portfolios.length === 0 ? (
            <div className="no-portfolios">
              <p>No portfolios available</p>
              {!isSystemAdmin() && <p className="hint">Ask your administrator for access</p>}
            </div>
          ) : (
            portfolios.map((p) => (
              <div key={p.id} className="sidebar-section">
                <div
                  className={`sidebar-item portfolio ${selectedPortfolio?.id === p.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedPortfolio(p);
                    setSelectedBuilding(null);
                    setShowTeamPanel(false);
                  }}
                >
                  <span className="tree-icon">{selectedPortfolio?.id === p.id ? '▼' : '▶'}</span>
                  {p.name}
                  {getPortfolioRole(p.members || {}) && (
                    <span className={`role-badge-small ${getPortfolioRole(p.members || {})}`}>
                      {getRoleDisplayName(getPortfolioRole(p.members || {}))}
                    </span>
                  )}
                </div>

                {selectedPortfolio?.id === p.id && (
                  <div className="buildings-list">
                    {buildings.map((b) => (
                      <div key={b.id} className="building-section">
                        <div
                          className={`sidebar-item building ${selectedBuilding?.id === b.id ? 'active' : ''}`}
                          onClick={() => { setSelectedBuilding(b); setSidebarOpen(false); }}
                        >
                          {b.name}
                        </div>
                      </div>
                    ))}
                    {canEdit && (
                      <div
                        className="sidebar-item add-new"
                        onClick={() => setShowNewBuilding(true)}
                      >
                        + Add Building
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Mobile header with menu button - only show when portfolio selected */}
        {selectedPortfolio && (
          <div className="mobile-header">
            <button
              className="mobile-menu-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle menu"
            >
              &#9776;
            </button>
            <div className="mobile-header-title">
              <span>{selectedPortfolio.name}</span>
              {selectedBuilding && ` / ${selectedBuilding.name}`}
            </div>
          </div>
        )}

        {!selectedPortfolio ? (
          portfolios.length > 0 ? (
            renderAllPortfoliosOverview()
          ) : (
          <>
            {/* Desktop version */}
            <div className="empty-state empty-state-desktop">
              <h2>No Portfolios</h2>
              {!isSystemAdmin() && (
                <p className="hint">You don't have access to any portfolios yet. Contact your administrator.</p>
              )}
              {isSystemAdmin() && (
                <p className="hint">Create a portfolio from the sidebar to get started.</p>
              )}
            </div>
            {/* Mobile version - tappable */}
            <div className="empty-state empty-state-mobile" onClick={() => setSidebarOpen(true)}>
              <div className="empty-state-icon">&#9776;</div>
              <h2>No Portfolios</h2>
              {!isSystemAdmin() && (
                <p className="hint">You don't have access to any portfolios yet. Contact your administrator.</p>
              )}
            </div>
          </>
          )
        ) : (
          <div className="portfolio-overview">
            <div className="portfolio-header">
              <h2>{selectedPortfolio.name}</h2>
              {myRole && (
                <span className={`my-role-badge ${myRole}`}>
                  {getRoleDisplayName(myRole)}
                </span>
              )}
              {isSystemAdmin() && !myRole && userProfile?.role && (
                <span className={`my-role-badge ${userProfile.role}`}>
                  {getRoleDisplayName(userProfile.role)}
                </span>
              )}
              {canManageUsers && (
                <button className="btn-team" onClick={() => setShowTeamPanel(true)}>
                  Manage Team
                </button>
              )}
              {canEdit && (
                <button className="btn-connection-alerts" onClick={() => { setViewingConnectionAlerts(true); setConnectionAlertsTab('status'); }}>
                  Connection Alerts
                </button>
              )}
              {canEdit && (
                <button className="btn-bulk-alerts" onClick={openBulkAlerts}>
                  Bulk Alert Contacts
                </button>
              )}
              {canEdit && (
                <button className="btn-bulk-settings" onClick={openBulkSettings}>
                  Bulk Alarm Settings
                </button>
              )}
              <div className="view-toggle">
                <button
                  className={portfolioView === 'cards' ? 'active' : ''}
                  onClick={() => switchPortfolioView('cards')}
                >
                  Cards
                </button>
                <button
                  className={portfolioView === 'table' ? 'active' : ''}
                  onClick={() => switchPortfolioView('table')}
                >
                  Table
                </button>
              </div>
            </div>
            {portfolioView === 'table' ? renderSensorTable() : (<>
            {/* Unassigned devices warning - hide when a building is selected */}
            {!selectedBuilding && devices.filter(d => !d.building_id).length > 0 && (
              <div className="unassigned-warning section">
                <h3>Unassigned Devices</h3>
                <p>These devices need to be assigned to a building:</p>
                <div className="card-grid">
                  {devices.filter(d => !d.building_id).map((d) => {
                    const deviceStatus = getDeviceStatus(d, offlineThresholdMinutes);
                    return (
                      <div key={d.id} className={`card device unassigned ${deviceStatus.status}`} onClick={() => canEdit && openDeviceSettings(d)}>
                        <div className="device-status-row">
                          <span className={`status-dot ${deviceStatus.status}`}></span>
                          <span className="last-seen">{deviceStatus.lastSeenText}</span>
                          <WifiSignal signal={d.wifi_signal} offline={deviceStatus.status === 'offline'} />
                        </div>
                        <h4>{d.name}</h4>
                        <p className="serial">{d.serial || d.id}</p>
                        {canEdit && <p className="assign-hint">Click to assign</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Buildings with devices and sensors inline */}
            {buildings.length === 0 ? (
              <div className="section">
                <p className="hint">No buildings yet. {canEdit ? 'Add one from the sidebar.' : ''}</p>
              </div>
            ) : (
              (selectedBuilding ? buildings.filter(b => b.id === selectedBuilding.id) : buildings).map((building) => {
                const buildingDevices = devices.filter(d => d.building_id === building.id);
                return (
                  <div key={building.id} className="building-section">
                    <div className="building-section-header">
                      <h3>{building.name}</h3>
                      {building.address && <span className="address">{building.address}</span>}
                      {canEdit && (
                        <button className="btn-add-sensor-inline" onClick={() => { setSelectedBuilding(building); setShowNewSensor(true); }}>
                          + Add Sensor
                        </button>
                      )}
                    </div>

                    {buildingDevices.length === 0 ? (
                      <p className="hint">No devices assigned to this building.</p>
                    ) : (
                      <div className="devices-list">
                        {buildingDevices.map((d) => {
                          const deviceSensors = sensors.filter(s => s.device_id === d.id);
                          const deviceStatus = getDeviceStatus(d, offlineThresholdMinutes);
                          return (
                            <div key={d.id} className={`device-block ${deviceStatus.status}`}>
                              <div className="device-header" onClick={() => canEdit && openDeviceSettings(d)}>
                                <div className="device-info">
                                  <span className={`status-dot ${deviceStatus.status}`}></span>
                                  <h4>{d.name}</h4>
                                  <span className="serial">{d.serial || d.id}</span>
                                  <span className="last-seen-inline">({deviceStatus.lastSeenText})</span>
                                  <WifiSignal signal={d.wifi_signal} offline={deviceStatus.status === 'offline'} />
                                </div>
                                <div className="device-meta">
                                  <span>{deviceSensors.length} sensor(s)</span>
                                  {canEdit && <span className="edit-hint">Click to edit</span>}
                                </div>
                              </div>
                              <div className="device-sensors-list">
                                {deviceSensors.length === 0 && (
                                  <div className="no-sensors-hint">No sensors configured yet</div>
                                )}
                                {deviceSensors.map((s) => {
                                  const tempDisplay = getSensorTempDisplay(s, d, offlineThresholdMinutes);
                                  const isOffline = ['Offline', 'Device Offline', 'Sensor Error'].includes(tempDisplay);
                                  const isAlert = !isOffline && s.current_temp && s.current_temp > (s.threshold || 80);
                                  return (
                                    <div key={s.id} className={`sensor-card-inline ${isOffline ? 'offline' : ''} ${isAlert ? 'alert' : ''}`} onClick={() => openSensorPage(s)}>
                                      <div className="sensor-main">
                                        <div className="sensor-name">{s.name}</div>
                                        <div className="sensor-id-small">{s.sensor_id || s.id}</div>
                                      </div>
                                      <div className={`sensor-temp ${isOffline ? 'offline' : ''} ${isAlert ? 'alert' : ''}`}>{tempDisplay}</div>
                                      <div className="sensor-details">
                                        <div className="sensor-threshold">Threshold: {s.threshold || 45}°F</div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
            </>)}
          </div>
        )}
      </div>

        {/* Add Sensor Modal - now works from portfolio view */}
        {showNewSensor && selectedBuilding && (
          <div className="modal-overlay" onClick={() => { setShowNewSensor(false); setSelectedBuilding(null); }}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Add Sensor to {selectedBuilding.name}</h3>
              <div className="form-group">
                <label>Sensor Name</label>
                <input
                  type="text"
                  value={newSensorName}
                  onChange={(e) => setNewSensorName(e.target.value)}
                  placeholder="e.g., Walk-in Cooler"
                />
              </div>
              <div className="form-group">
                <label>Device</label>
                <select value={selectedDevice} onChange={(e) => { setSelectedDevice(e.target.value); setSelectedSensorId(''); }}>
                  <option value="">Select device...</option>
                  {devices.filter(d => d.building_id === selectedBuilding.id).map((d) => (
                    <option key={d.id} value={d.id}>{d.name} ({d.serial || d.id})</option>
                  ))}
                </select>
              </div>
              {selectedDevice && (
                <div className="form-group">
                  <label>Physical Sensor</label>
                  <select value={selectedSensorId} onChange={(e) => setSelectedSensorId(e.target.value)}>
                    <option value="">Select sensor...</option>
                    {(devices.find(d => d.id === selectedDevice)?.sensors || []).map((sensorId) => (
                      <option key={sensorId} value={sensorId}>{sensorId}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-actions">
                <button className="btn-primary" onClick={handleAddSensor}>Add Sensor</button>
                <button className="btn-secondary" onClick={() => { setShowNewSensor(false); setSelectedBuilding(null); setNewSensorName(''); setSelectedDevice(''); setSelectedSensorId(''); }}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      {/* New Building Modal */}
      {showNewBuilding && canEdit && (
        <div className="modal-overlay" onClick={() => setShowNewBuilding(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>New Building</h3>
            <div className="form-group">
              <label>Building Name</label>
              <input
                type="text"
                value={newBuildingName}
                onChange={(e) => setNewBuildingName(e.target.value)}
                placeholder="e.g., Downtown Location"
              />
            </div>
            <div className="form-group">
              <label>Address (optional)</label>
              <input
                type="text"
                value={newBuildingAddress}
                onChange={(e) => setNewBuildingAddress(e.target.value)}
                placeholder="e.g., 123 Main St"
              />
            </div>
            <div className="form-actions">
              <button className="btn-primary" onClick={handleCreateBuilding}>Create</button>
              <button className="btn-secondary" onClick={() => setShowNewBuilding(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* New Portfolio Modal (Super Admin) */}
      {showNewPortfolio && isSystemAdmin() && (
        <div className="modal-overlay" onClick={() => setShowNewPortfolio(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>New Portfolio</h3>
            <div className="form-group">
              <label>Portfolio Name</label>
              <input
                type="text"
                value={newPortfolioName}
                onChange={(e) => setNewPortfolioName(e.target.value)}
                placeholder="e.g., ABC Restaurant Group"
              />
            </div>
            <div className="form-actions">
              <button className="btn-primary" onClick={handleCreatePortfolio}>Create</button>
              <button className="btn-secondary" onClick={() => setShowNewPortfolio(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Device Settings Modal */}
      {editingDevice && canEdit && (
        <div className="modal-overlay" onClick={() => setEditingDevice(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Device Settings</h3>
            <div className="form-group">
              <label>Device Name</label>
              <input
                type="text"
                value={editDeviceName}
                onChange={(e) => setEditDeviceName(e.target.value)}
                placeholder="e.g., Kitchen Pi"
              />
            </div>
            <div className="form-group">
              <label>Assigned Building <span className="optional">(optional)</span></label>
              <select
                value={editDeviceBuilding}
                onChange={(e) => setEditDeviceBuilding(e.target.value)}
              >
                <option value="">Not assigned to a building</option>
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="sensor-info-row">
              <span className="label">Device Serial Number:</span>
              <span className="value mono">{editingDevice.mac || editingDevice.id}</span>
            </div>
            <div className="sensor-info-row">
              <span className="label">Physical Sensors:</span>
              <span className="value">{editingDevice.sensors?.length || 0}</span>
            </div>
            {editingDevice.sensors?.length > 0 && (
              <div className="device-sensor-list">
                {editingDevice.sensors.map((sId) => (
                  <span key={sId} className="sensor-tag-small">{sId}</span>
                ))}
              </div>
            )}
            <div className="form-actions">
              <button className="btn-primary" onClick={handleSaveDevice}>Save</button>
              <button className="btn-secondary" onClick={() => setEditingDevice(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleDeleteDevice}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .portfolios-layout {
          display: flex;
          height: calc(100vh - 60px);
        }
        .sidebar {
          width: 280px;
          background: #1a1a2e;
          border-right: 1px solid #3a3a5a;
          display: flex;
          flex-direction: column;
        }
        .sidebar-header {
          padding: 15px;
          border-bottom: 1px solid #3a3a5a;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .sidebar-header h3 {
          margin: 0;
          flex: 1;
        }
        .sidebar-close-btn {
          display: none;
          background: transparent;
          border: none;
          color: #888;
          font-size: 20px;
          cursor: pointer;
          padding: 5px;
          line-height: 1;
        }
        .sidebar-close-btn:hover {
          color: #fff;
          font-size: 16px;
          color: #4ecdc4;
        }
        .add-portfolio-btn {
          background: #4ecdc4;
          color: #1a1a2e;
          border: none;
          width: 28px;
          height: 28px;
          border-radius: 4px;
          font-size: 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .add-portfolio-btn:hover {
          background: #3dbdb5;
        }
        .sidebar-content {
          flex: 1;
          overflow-y: auto;
          padding: 10px 0;
        }
        .no-portfolios {
          padding: 20px;
          text-align: center;
          color: #888;
        }
        .sidebar-item {
          padding: 10px 15px;
          cursor: pointer;
          transition: background 0.2s;
          color: #fff;
          display: flex;
          align-items: center;
        }
        .sidebar-item:hover {
          background: #2a2a4a;
        }
        .sidebar-item.active {
          background: #4ecdc4;
          color: #1a1a2e;
        }
        .sidebar-item.portfolio {
          font-weight: 600;
        }
        .sidebar-item.building {
          padding-left: 30px;
          font-size: 14px;
          color: #ccc;
          margin: 4px 8px;
          border-radius: 6px;
        }
        .sidebar-item.building.active {
          color: #1a1a2e;
        }
        .buildings-list {
          padding: 8px 0;
        }
        .sidebar-item.add-new {
          padding-left: 30px;
          font-size: 13px;
          color: #4ecdc4;
        }
        .sidebar-item.add-new:hover {
          background: #2a2a4a;
        }
        .sidebar-item.team-link {
          padding-left: 30px;
          font-size: 13px;
          color: #3498db;
        }
        .sidebar-item.team-link:hover {
          background: #2a2a4a;
        }
        .tree-icon {
          font-size: 10px;
          margin-right: 8px;
          opacity: 0.7;
        }
        .role-badge-small {
          margin-left: auto;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          white-space: nowrap;
        }
        .role-badge-small.customer_admin {
          background: #3498db;
          color: #fff;
        }
        .role-badge-small.customer_technician {
          background: #2ecc71;
          color: #fff;
        }
        .role-badge-small.customer_viewer {
          background: #95a5a6;
          color: #fff;
        }
        .building-children {
          background: #0f0f1a;
        }
        .sidebar-item.child-item {
          padding-left: 50px;
          font-size: 12px;
          color: #888;
          cursor: default;
        }
        .sidebar-item.child-item:hover {
          background: transparent;
        }
        .child-icon {
          margin-right: 6px;
        }
        .device-sensors {
          margin-top: 8px;
        }
        .sensor-tag-small {
          display: inline-block;
          background: #3a3a5a;
          padding: 2px 6px;
          border-radius: 3px;
          font-family: monospace;
          font-size: 9px;
          margin: 2px;
          color: #4ecdc4;
        }
        .card.clickable {
          cursor: pointer;
          transition: border-color 0.2s, transform 0.1s;
        }
        .card.clickable:hover {
          border-color: #4ecdc4;
          transform: translateY(-2px);
        }
        .device-sensor-list {
          padding: 10px 0;
        }
        .main-content {
          flex: 1;
          padding: 20px;
          overflow-y: auto;
          background: #16213e;
        }
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #888;
        }
        .empty-state-mobile {
          display: none;
        }
        .empty-state.small {
          padding: 40px 20px;
        }
        .empty-state h2 {
          margin: 0 0 10px 0;
          color: #fff;
        }
        .hint {
          font-size: 14px;
          color: #888;
        }
        .portfolio-header {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px 15px;
          margin-bottom: 5px;
        }
        .portfolio-overview h2 {
          margin: 0;
          color: #fff;
        }
        .building-section {
          background: #1a1a2e;
          border: 1px solid #3a3a5a;
          border-radius: 8px;
          margin-bottom: 20px;
          overflow: hidden;
        }
        .building-section-header {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 15px 20px;
          background: #0f0f1a;
          border-bottom: 1px solid #3a3a5a;
        }
        .building-section-header h3 {
          margin: 0;
          color: #4ecdc4;
          font-size: 18px;
        }
        .building-section-header .address {
          color: #888;
          font-size: 14px;
        }
        .building-section-header .btn-add-sensor-inline {
          margin-left: auto;
          background: #4ecdc4;
          color: #1a1a2e;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 600;
          font-size: 13px;
        }
        .building-section-header .btn-add-sensor-inline:hover {
          background: #3dbdb5;
        }
        .building-section .devices-list {
          padding: 15px;
        }
        .building-section .hint {
          padding: 20px;
          color: #666;
          text-align: center;
        }
        .serial {
          font-family: monospace;
          font-size: 11px;
          color: #4ecdc4;
        }
        .my-role-badge {
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
        }
        .my-role-badge.customer_admin {
          background: #3498db;
          color: #fff;
        }
        .my-role-badge.customer_technician {
          background: #2ecc71;
          color: #fff;
        }
        .my-role-badge.customer_viewer {
          background: #95a5a6;
          color: #fff;
        }
        .my-role-badge.super_admin {
          background: #e74c3c;
          color: #fff;
        }
        .my-role-badge.admin {
          background: #9b59b6;
          color: #fff;
        }
        .btn-team {
          background: #3498db;
          color: #fff;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 600;
          font-size: 13px;
          margin-left: auto;
        }
        .btn-team:hover {
          background: #2980b9;
        }
        .subtitle {
          color: #888;
          margin: 0 0 30px 0;
        }
        .section {
          margin-bottom: 30px;
        }
        .section h3 {
          margin: 0 0 15px 0;
          color: #4ecdc4;
        }
        .card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 15px;
        }
        .card {
          background: #2a2a4a;
          border: 1px solid #3a3a5a;
          border-radius: 8px;
          padding: 15px;
          cursor: pointer;
          transition: border-color 0.2s;
        }
        .card:hover {
          border-color: #4ecdc4;
        }
        .card h4 {
          margin: 0 0 5px 0;
          color: #fff;
        }
        .card p {
          margin: 0;
          font-size: 13px;
          color: #888;
        }
        .card.device .mac {
          font-family: monospace;
          font-size: 11px;
          color: #4ecdc4;
        }
        .card.device.unassigned {
          border-color: #ff6b6b;
          cursor: pointer;
        }
        .card.device.unassigned:hover {
          background: #3a3a5a;
        }
        .card.device .assign-hint {
          color: #ff6b6b;
          font-size: 11px;
          margin-top: 8px;
        }
        .card.device .building-tag {
          color: #4ecdc4;
          font-size: 11px;
          margin-top: 8px;
        }
        /* Device status indicators */
        .device-status-row {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
        }
        .status-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }
        .status-dot.online {
          background: #2ecc71;
          box-shadow: 0 0 8px rgba(46, 204, 113, 0.6);
          animation: pulse-online 2s ease-in-out infinite;
        }
        .status-dot.offline {
          background: #e74c3c;
          box-shadow: 0 0 8px rgba(231, 76, 60, 0.6);
          animation: pulse-offline 1.5s ease-in-out infinite;
        }
        .status-dot.unknown {
          background: #95a5a6;
        }
        @keyframes pulse-online {
          0%, 100% { opacity: 1; box-shadow: 0 0 8px rgba(46, 204, 113, 0.6); }
          50% { opacity: 0.7; box-shadow: 0 0 12px rgba(46, 204, 113, 0.8); }
        }
        @keyframes pulse-offline {
          0%, 100% { opacity: 1; box-shadow: 0 0 8px rgba(231, 76, 60, 0.6); }
          50% { opacity: 0.6; box-shadow: 0 0 14px rgba(231, 76, 60, 0.9); }
        }
        .last-seen {
          font-size: 11px;
          color: #888;
        }
        .last-seen-inline {
          font-size: 11px;
          color: #888;
          margin-left: 8px;
        }
        .wifi-signal {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-left: 8px;
          font-size: 11px;
          color: #555;
        }
        .wifi-label {
          font-weight: 600;
          letter-spacing: 0.3px;
          text-transform: uppercase;
          font-size: 10px;
          opacity: 0.85;
        }
        .wifi-bars {
          display: inline-flex;
          align-items: flex-end;
          gap: 1px;
          height: 12px;
        }
        .wifi-bar {
          width: 3px;
          border-radius: 1px;
          background: #d0d0d0;
        }
        .wifi-bar:nth-child(1) { height: 3px; }
        .wifi-bar:nth-child(2) { height: 6px; }
        .wifi-bar:nth-child(3) { height: 9px; }
        .wifi-bar:nth-child(4) { height: 12px; }
        .wifi-signal.excellent .wifi-bar.on { background: #2ecc71; }
        .wifi-signal.good .wifi-bar.on { background: #7ed957; }
        .wifi-signal.fair .wifi-bar.on { background: #f39c12; }
        .wifi-signal.poor .wifi-bar.on { background: #e74c3c; }
        .wifi-signal.excellent { color: #2ecc71; }
        .wifi-signal.good { color: #56a649; }
        .wifi-signal.fair { color: #c87f0a; }
        .wifi-signal.poor { color: #e74c3c; }
        .wifi-percent {
          font-variant-numeric: tabular-nums;
        }
        .card.device.offline {
          border-color: #e74c3c;
        }
        .device-block.offline {
          border-left: 4px solid #e74c3c;
          background: rgba(231, 76, 60, 0.12);
          animation: pulse-device-offline 2s ease-in-out infinite;
          box-shadow: inset 0 0 20px rgba(231, 76, 60, 0.1);
        }
        .device-block.offline .device-header {
          background: rgba(231, 76, 60, 0.08);
        }
        @keyframes pulse-device-offline {
          0%, 100% { background: rgba(231, 76, 60, 0.12); }
          50% { background: rgba(231, 76, 60, 0.18); }
        }
        .device-block.online {
          border-left: 3px solid #2ecc71;
        }
        .device-block {
          transition: all 0.3s ease;
        }
        .device-block:hover {
          background: rgba(78, 205, 196, 0.05);
        }
        .device-block.offline:hover {
          background: rgba(231, 76, 60, 0.2);
        }
        /* Connection settings button */
        .btn-connection-alerts {
          background: #9b59b6;
          color: #fff;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
        }
        .btn-connection-alerts:hover {
          background: #8e44ad;
        }
        .btn-bulk-alerts {
          background: #2980b9;
          color: #fff;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
        }
        .btn-bulk-alerts:hover {
          background: #1f6391;
        }
        .btn-bulk-settings {
          background: #16a085;
          color: #fff;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
        }
        .btn-bulk-settings:hover {
          background: #117a65;
        }
        /* Connection settings modal */
        .modal-subtitle {
          color: #888;
          margin: -10px 0 20px 0;
          font-size: 14px;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
        }
        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
        .device-status-summary {
          background: #2a2a4a;
          border-radius: 8px;
          padding: 15px;
          margin: 20px 0;
        }
        .device-status-summary h4 {
          margin: 0 0 10px 0;
          color: #fff;
          font-size: 14px;
        }
        .status-counts {
          display: flex;
          gap: 15px;
        }
        .status-count {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
        }
        .status-count::before {
          content: '';
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }
        .status-count.online::before {
          background: #2ecc71;
        }
        .status-count.offline::before {
          background: #e74c3c;
        }
        .status-count.unknown::before {
          background: #95a5a6;
        }
        /* Connection Alerts Page */
        .connection-alerts-page .connection-status-summary {
          display: flex;
          gap: 10px;
        }
        .status-badge {
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
        }
        .status-badge.online {
          background: rgba(46, 204, 113, 0.15);
          color: #2ecc71;
          border: 1px solid rgba(46, 204, 113, 0.3);
        }
        .status-badge.offline {
          background: rgba(231, 76, 60, 0.15);
          color: #e74c3c;
          border: 1px solid rgba(231, 76, 60, 0.3);
        }
        .status-panel, .logs-panel {
          max-width: 800px;
          margin: 0 auto;
        }
        .status-section {
          background: #1a1a2e;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }
        .status-section h3 {
          margin: 0 0 15px 0;
          color: #4ecdc4;
          font-size: 16px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .offline-section {
          border: 1px solid rgba(231, 76, 60, 0.3);
        }
        .offline-section h3 {
          color: #e74c3c;
        }
        .online-section {
          border: 1px solid rgba(46, 204, 113, 0.2);
        }
        .online-section h3 {
          color: #2ecc71;
        }
        .device-status-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 12px;
        }
        .device-status-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: #16213e;
          border-radius: 8px;
          border: 1px solid #3a3a5a;
        }
        .device-status-card.online {
          border-left: 3px solid #2ecc71;
        }
        .device-status-card.offline {
          border-left: 3px solid #e74c3c;
          background: rgba(231, 76, 60, 0.05);
        }
        .device-status-info {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .device-status-info .device-name {
          font-weight: 600;
          color: #fff;
          display: block;
        }
        .device-status-info .device-serial {
          font-size: 12px;
          color: #4ecdc4;
          font-family: monospace;
          display: block;
        }
        .device-last-seen {
          font-size: 12px;
          color: #888;
          white-space: nowrap;
        }
        /* Event Log */
        .logs-panel h3 {
          color: #4ecdc4;
          margin: 0 0 20px 0;
        }
        .events-list {
          background: #1a1a2e;
          border-radius: 8px;
          overflow: hidden;
        }
        .event-item {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 14px 16px;
          border-bottom: 1px solid #2a2a4a;
        }
        .event-item:last-child {
          border-bottom: none;
        }
        .event-icon {
          font-size: 18px;
          width: 24px;
          text-align: center;
        }
        .event-details {
          flex: 1;
        }
        .event-device {
          font-weight: 600;
          color: #fff;
        }
        .event-type {
          color: #888;
          margin-left: 6px;
        }
        .event-duration {
          color: #666;
          font-size: 12px;
          margin-left: 6px;
        }
        .event-time {
          font-size: 12px;
          color: #666;
        }
        /* Connection Contacts - use existing contact-card styles */
        .connection-alerts-page .contacts-panel {
          max-width: 800px;
          margin: 0 auto;
        }
        .connection-alerts-page .contacts-panel h3 {
          color: #4ecdc4;
          margin: 0 0 10px 0;
        }
        .connection-alerts-page .contacts-list {
          margin-top: 20px;
        }
        .connection-alerts-page .contact-card {
          background: #1a1a2e;
          border: 1px solid #3a3a5a;
        }
        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .contact-name {
          font-weight: 500;
          color: #fff;
        }
        .contact-email {
          font-size: 13px;
          color: #4ecdc4;
        }
        .contact-actions {
          display: flex;
          gap: 8px;
        }
        .btn-small {
          padding: 6px 12px;
          font-size: 12px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          background: #3a3a5a;
          color: #fff;
        }
        .btn-small:hover {
          background: #4a4a6a;
        }
        .btn-small.danger {
          background: rgba(231, 76, 60, 0.3);
          color: #e74c3c;
        }
        .btn-small.danger:hover {
          background: rgba(231, 76, 60, 0.5);
        }
        /* Connection Settings panel */
        .connection-alerts-page .settings-panel {
          max-width: 800px;
          margin: 0 auto;
        }
        .connection-alerts-page .settings-panel .form-group {
          background: #1a1a2e;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 15px;
        }
        .connection-alerts-page .settings-panel .info-section {
          background: #1a1a2e;
          border: 1px solid #3a3a5a;
        }
        .unassigned-warning {
          background: rgba(255, 107, 107, 0.1);
          border: 1px solid #ff6b6b;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 20px;
        }
        .unassigned-warning h4 {
          color: #ff6b6b;
          margin: 0 0 5px 0;
        }
        .unassigned-warning p {
          color: #ccc;
          margin: 0 0 15px 0;
          font-size: 14px;
        }
        .building-header {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 25px;
        }
        .building-header h2 {
          margin: 0;
          color: #fff;
        }
        .building-header .address {
          margin: 0;
          color: #888;
          flex: 1;
        }
        .btn-primary {
          background: #4ecdc4;
          color: #1a1a2e;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 600;
        }
        .btn-primary:hover {
          background: #3dbdb5;
        }
        .btn-secondary {
          background: #3a3a5a;
          color: #fff;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
        }
        .btn-secondary:hover {
          background: #4a4a6a;
        }
        .new-form {
          background: #2a2a4a;
          border: 1px solid #3a3a5a;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }
        .new-form h3 {
          margin: 0 0 15px 0;
          color: #4ecdc4;
        }
        .form-group {
          margin-bottom: 15px;
        }
        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 600;
          font-size: 14px;
          color: #fff;
        }
        .form-group label .optional {
          font-weight: 400;
          color: #888;
          font-size: 12px;
        }
        .form-group input, .form-group select {
          width: 100%;
          padding: 10px;
          border: 1px solid #3a3a5a;
          border-radius: 4px;
          font-size: 14px;
          background: #1a1a2e;
          color: #fff;
        }
        .form-group input:focus, .form-group select:focus {
          outline: none;
          border-color: #4ecdc4;
        }
        .form-actions {
          display: flex;
          gap: 10px;
        }
        .sensors-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 220px));
          gap: 20px;
        }
        .sensor-card {
          background: #2a2a4a;
          border: 1px solid #3a3a5a;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.2s, transform 0.1s;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .sensor-card:hover {
          border-color: #4ecdc4;
          transform: translateY(-2px);
        }
        .sensor-name {
          font-weight: 600;
          margin-bottom: 8px;
          color: #fff;
          font-size: 14px;
        }
        .sensor-temp {
          font-size: 36px;
          font-weight: bold;
          color: #4ecdc4;
          margin-bottom: 10px;
        }
        .sensor-threshold {
          font-size: 12px;
          color: #888;
          margin-bottom: 6px;
        }
        .sensor-id {
          font-size: 10px;
          color: #666;
          font-family: monospace;
          margin-bottom: 6px;
          word-break: break-all;
        }
        .sensor-alerts {
          font-size: 11px;
          color: #888;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal {
          background: #2a2a4a;
          border-radius: 8px;
          padding: 25px;
          width: 400px;
          max-width: 90%;
        }
        .modal h3 {
          margin: 0 0 20px 0;
          color: #4ecdc4;
        }
        .btn-danger {
          background: #e74c3c;
          color: #fff;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          margin-left: auto;
        }
        .btn-danger:hover {
          background: #c0392b;
        }
        .sensor-info-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-top: 1px solid #3a3a5a;
          font-size: 13px;
        }
        .sensor-info-row .label {
          color: #888;
        }
        .sensor-info-row .value {
          color: #fff;
        }
        .sensor-info-row .mono {
          font-family: monospace;
          color: #4ecdc4;
        }
        .loading {
          text-align: center;
          padding: 40px;
          color: #888;
        }
        .devices-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .device-block {
          background: #1a1a2e;
          border-radius: 8px;
          overflow: hidden;
        }
        .device-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          background: #2a2a4a;
          cursor: pointer;
          transition: background 0.2s;
        }
        .device-header:hover {
          background: #3a3a5a;
        }
        .device-info h4 {
          margin: 0 0 4px 0;
          color: #fff;
        }
        .device-info .mac {
          font-family: monospace;
          font-size: 12px;
          color: #4ecdc4;
        }
        .device-meta {
          text-align: right;
          font-size: 13px;
          color: #888;
        }
        .device-meta .edit-hint {
          display: block;
          font-size: 11px;
          color: #4ecdc4;
          margin-top: 4px;
        }
        .device-sensors-list {
          padding: 15px 20px;
        }
        .no-sensors-msg,
        .no-sensors-hint {
          color: #888;
          font-size: 14px;
          padding: 10px 0;
        }
        .sensor-card-inline {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 15px 18px;
          background: linear-gradient(145deg, #1a1a35 0%, #16213e 100%);
          border-radius: 10px;
          margin-bottom: 10px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(78, 205, 196, 0.1);
          position: relative;
          overflow: hidden;
        }
        .sensor-card-inline::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 3px;
          height: 100%;
          background: linear-gradient(180deg, #4ecdc4, #44a08d);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .sensor-card-inline:hover {
          border-color: rgba(78, 205, 196, 0.4);
          transform: translateX(6px);
          box-shadow: 0 4px 20px rgba(78, 205, 196, 0.15);
        }
        .sensor-card-inline:hover::before {
          opacity: 1;
        }
        .sensor-card-inline.offline {
          border-color: rgba(231, 76, 60, 0.4);
          background: linear-gradient(145deg, rgba(231, 76, 60, 0.15), rgba(231, 76, 60, 0.08));
          animation: pulse-sensor-offline 2s ease-in-out infinite;
        }
        .sensor-card-inline.offline::before {
          background: #e74c3c;
          opacity: 1;
          width: 4px;
          box-shadow: 0 0 10px rgba(231, 76, 60, 0.5);
        }
        @keyframes pulse-sensor-offline {
          0%, 100% { border-color: rgba(231, 76, 60, 0.4); }
          50% { border-color: rgba(231, 76, 60, 0.7); }
        }
        .sensor-card-inline.alert {
          border-color: rgba(255, 107, 107, 0.4);
          background: linear-gradient(145deg, rgba(255, 107, 107, 0.08), #16213e);
        }
        .sensor-card-inline.alert::before {
          background: linear-gradient(180deg, #ff6b6b, #ee5a24);
          opacity: 1;
        }
        .sensor-card-inline:last-child {
          margin-bottom: 0;
        }
        .sensor-card-inline .sensor-main {
          flex: 1;
        }
        .sensor-card-inline .sensor-name {
          font-weight: 600;
          color: #fff;
          margin-bottom: 4px;
        }
        .sensor-card-inline .sensor-id-small {
          font-family: monospace;
          font-size: 11px;
          color: #666;
        }
        .sensor-card-inline .sensor-temp {
          font-size: 28px;
          font-weight: bold;
          color: #4ecdc4;
          min-width: 100px;
          text-align: center;
          transition: color 0.3s;
        }
        .sensor-card-inline .sensor-temp.offline {
          color: #e74c3c;
          font-size: 18px;
          font-weight: 600;
          text-transform: uppercase;
        }
        .sensor-card-inline .sensor-temp.alert {
          color: #ff6b6b;
        }
        .sensor-card-inline .sensor-details {
          text-align: right;
          min-width: 140px;
        }
        .sensor-card-inline .sensor-threshold {
          font-size: 12px;
          color: #888;
        }
        .sensor-card-inline .sensor-alerts {
          font-size: 11px;
          color: #666;
          margin-top: 4px;
        }
        .sensors-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 15px;
        }
        .sensor-card-building {
          background: #1a1a2e;
          border-radius: 10px;
          padding: 20px;
          cursor: pointer;
          transition: all 0.2s;
          border: 2px solid transparent;
        }
        .sensor-card-building:hover {
          border-color: #4ecdc4;
          transform: translateY(-2px);
        }
        .sensor-card-building .sensor-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 15px;
        }
        .sensor-card-building .sensor-name {
          font-weight: 600;
          color: #fff;
          font-size: 16px;
        }
        .sensor-card-building .sensor-temp-large {
          font-size: 32px;
          font-weight: bold;
          color: #4ecdc4;
        }
        .sensor-card-building .sensor-meta {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: #888;
        }
        .sensor-card-building .sensor-device {
          color: #666;
        }

        /* Mobile hamburger menu button */
        .mobile-menu-btn {
          display: none;
          background: #4ecdc4;
          color: #1a1a2e;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 6px;
          font-size: 20px;
          cursor: pointer;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .mobile-menu-btn:hover {
          background: #3dbdb5;
        }

        /* Sidebar overlay for mobile */
        .sidebar-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 999;
        }

        /* Mobile header bar inside main content */
        .mobile-header {
          display: none;
          padding: 12px 15px;
          background: #1a1a2e;
          border-radius: 8px;
          margin-bottom: 15px;
          align-items: center;
          gap: 12px;
        }
        .mobile-header-title {
          flex: 1;
          font-size: 14px;
          color: #fff;
        }
        .mobile-header-title span {
          color: #4ecdc4;
        }

        /* Mobile responsive styles */
        @media (max-width: 768px) {
          .mobile-menu-btn {
            display: flex;
          }
          .mobile-header {
            display: flex;
          }
          .sidebar-close-btn {
            display: block;
          }
          .sidebar-overlay {
            display: block;
          }
          .empty-state-desktop {
            display: none;
          }
          .empty-state-mobile {
            display: block;
          }
          .portfolios-layout {
            display: block;
          }
          .sidebar {
            position: fixed;
            top: 0;
            left: -100%;
            bottom: 0;
            width: 280px;
            max-width: 85vw;
            z-index: 1000;
            transition: left 0.3s ease;
          }
          .sidebar.open {
            left: 0;
          }
          .main-content {
            padding: 15px;
            width: 100%;
            min-height: calc(100vh - 250px);
          }
          .portfolio-header {
            flex-wrap: wrap;
          }
          .building-header {
            flex-wrap: wrap;
            gap: 10px;
          }
          .building-header h2 {
            width: 100%;
          }
          .sensors-grid {
            grid-template-columns: 1fr;
          }
          .device-header {
            flex-wrap: wrap;
            gap: 10px;
          }
          .device-header-info {
            width: 100%;
          }
          .sensor-card-inline {
            min-width: 100%;
          }
          .card-grid {
            grid-template-columns: 1fr;
          }
          .device-status-list {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .sidebar {
            width: 100%;
            max-width: 100%;
          }
          .sensor-card {
            padding: 15px;
          }
          .sensor-temp {
            font-size: 28px;
          }
          .building-section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
          .building-section-header .btn-add-sensor-inline {
            margin-left: 0;
            width: 100%;
          }
        }

        /* --- View toggle (Cards | Table) --- */
        .view-toggle {
          display: inline-flex;
          margin-left: auto;
          flex-shrink: 0;
          border: 1px solid #4a5a8a;
          border-radius: 6px;
          overflow: hidden;
        }
        .view-toggle button {
          background: transparent;
          border: none;
          color: #8fa3c8;
          padding: 8px 16px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
        }
        .view-toggle button.active {
          background: #0f3460;
          color: #fff;
        }
        .view-toggle button:hover:not(.active) {
          background: rgba(15, 52, 96, 0.4);
        }

        /* --- Compact sensor table view --- */
        .sensor-table-wrap {
          overflow-x: auto;
        }
        .sensor-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }
        .sensor-table th {
          text-align: left;
          padding: 10px 12px;
          color: #8fa3c8;
          font-weight: 600;
          border-bottom: 2px solid #0f3460;
          white-space: nowrap;
        }
        .sensor-table th.sortable {
          cursor: pointer;
          user-select: none;
        }
        .sensor-table th.sortable:hover {
          color: #c8d6f0;
        }
        .sensor-table .sort-arrow {
          font-size: 10px;
          color: #4a90d9;
        }
        .sensor-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #1f2b4d;
          white-space: nowrap;
        }
        .sensor-table .sensor-row {
          cursor: pointer;
        }
        .sensor-table .sensor-row:hover td {
          background: rgba(15, 52, 96, 0.35);
        }
        .sensor-table .cell-sensor {
          font-weight: 600;
        }
        .sensor-table .cell-temp {
          font-weight: 700;
          color: #2ecc71;
        }
        .sensor-table .cell-temp.alert {
          color: #e74c3c;
        }
        .sensor-table .cell-temp.offline {
          color: #8fa3c8;
          font-weight: 400;
        }
        .status-dot.alert {
          background: #f39c12;
          box-shadow: 0 0 6px rgba(243, 156, 18, 0.6);
        }

        /* --- All-portfolios overview --- */
        .sidebar-item.all-portfolios {
          font-weight: 600;
          border-bottom: 1px solid #3a3a5a;
          margin-bottom: 8px;
        }
        .overview-cards {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 16px;
          margin-top: 10px;
        }
        .overview-card {
          background: #1a1a2e;
          border: 1px solid #3a3a5a;
          border-radius: 10px;
          padding: 18px;
          cursor: pointer;
          transition: border-color 0.15s, transform 0.1s;
        }
        .overview-card:hover {
          border-color: #4a90d9;
          transform: translateY(-2px);
        }
        .overview-card.has-alert {
          border-color: #e74c3c;
        }
        .overview-card.has-offline:not(.has-alert) {
          border-color: #f39c12;
        }
        .overview-card h3 {
          margin: 0 0 10px;
          color: #fff;
        }
        .overview-stats {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          font-size: 13px;
        }
        .overview-stats .stat {
          background: #16213e;
          border-radius: 4px;
          padding: 3px 8px;
          color: #8fa3c8;
        }
        .overview-stats .stat.alert {
          color: #fff;
          background: #e74c3c;
          font-weight: 600;
        }
        .overview-stats .stat.offline {
          color: #1a1a2e;
          background: #f39c12;
          font-weight: 600;
        }
        .overview-stats .stat.ok {
          color: #2ecc71;
        }
      `}</style>
    </div>
  );
}

export default Portfolios;
